import type { DocumentLayerEntry, EditorPage, TemplateLayer } from '../types/editor';
import { prepareTextLayout } from '@/shared/canvas-engine/layers/text';
import type { TextLayer as CanvasEngineTextLayer } from '@/shared/canvas-engine/types';

/**
 * Polished multi-page PDF starter templates.
 *
 * Every template is authored against a shared design system so the set feels
 * like a family of documents from one studio:
 *  - US Letter pages at 96dpi (816 × 1056 px) with 64px margins.
 *  - Typographic scale: display 34-40, page title 30, section 14-20,
 *    body 11-12, captions 8.5-10 with letter-spaced uppercase kickers.
 *  - One accent color per template from a curated print palette; light paper
 *    backgrounds with thin rules, quiet cards, and generous white space.
 *  - Long-form legal bodies (contract / NDA) flow through document-level
 *    FlowText layers; structured pages (tables, KPI cards, signatures) are
 *    built from rect + text + graph layers.
 *
 * Layer authoring notes (these conventions are load-bearing):
 *  - Seed layers are stored in painter order (index 0 = background). The
 *    editor stores layers top-first; buildPolishedPdfTemplate reverses.
 *  - Text layers are authored with `__seedTextAnchor: 'top-left'` and are
 *    converted to the canvas engine's center-based coordinates at build time
 *    using real text metrics (see normalizeSeedTextLayerAnchor).
 *  - `mode: 'line'` text must be single-line (no \n) — multi-line copy uses
 *    `mode: 'box'` with an explicit boxHeight, otherwise the engine's
 *    constrain solver collapses the font to minFont.
 */

export type PolishedPdfTemplateId =
  | 'client-proposal-pack'
  | 'service-contract-pack'
  | 'service-invoice-pack'
  | 'monthly-report-pack'
  | 'nda-pack'
  | 'statement-of-work-pack'
  | 'client-onboarding-pack'
  | 'case-study-pack'
  | 'meeting-minutes-pack'
  | 'media-kit-pack';

type TemplateSeedPage = {
  label: string;
  width: number;
  height: number;
  layers: Array<Record<string, unknown>>;
};

type TemplateSeed = {
  id: PolishedPdfTemplateId;
  title: string;
  shortLabel: string;
  description: string;
  icon: string;
  composerPresetId: string;
  pages: TemplateSeedPage[];
  /** Optional builder for document-spanning FlowText layers. Called with a unique nonce at build time. */
  buildDocumentLayers?: (nonce: string) => DocumentLayerEntry[];
};

export type PolishedPdfTemplatePreset = {
  id: PolishedPdfTemplateId;
  title: string;
  shortLabel: string;
  description: string;
  icon: string;
  composerPresetId: string;
  pageCount: number;
};

export type BuiltPolishedPdfTemplate = {
  id: PolishedPdfTemplateId;
  title: string;
  composerPresetId: string;
  pages: EditorPage[];
  layersByPageId: Record<string, TemplateLayer[]>;
  documentLayers: DocumentLayerEntry[];
};

/* ── Design system ─────────────────────────────────────────────────────── */

const PAGE_W = 816;
const PAGE_H = 1056;
const LETTER_PAGE = { width: PAGE_W, height: PAGE_H };
const M = 64; // page margin
const CW = PAGE_W - M * 2; // 688 content width

const INK = '#111827';
const BODY_COLOR = '#374151';
const MUTED = '#6b7280';
const FAINT = '#9ca3af';
const RULE = '#e5e7eb';
const CARD = '#f8fafc';
const OK_GREEN = '#15803d';

const SANS = 'Inter, sans-serif';
const SERIF = 'Georgia, serif';

// FlowText content window shared by long-form legal documents.
const FLOW_TOP = 96;
const FLOW_BOTTOM = 96;

type SeedLayer = Record<string, unknown>;

/* ── Layer factories (top-left page coordinates in, centered coords out) ── */

type TextOpts = {
  name: string;
  left: number;
  top: number;
  width: number;
  text: string;
  size: number;
  weight?: number;
  color?: string;
  align?: 'left' | 'center' | 'right';
  family?: string;
  italic?: boolean;
  tracking?: number;
  lineHeight?: number;
};

/** Single-line text layer (mode 'line'). Never pass \n here. */
function text(o: TextOpts): SeedLayer {
  return {
    name: o.name,
    type: 'text',
    __seedTextAnchor: 'top-left',
    x: o.left - PAGE_W / 2,
    y: o.top - PAGE_H / 2,
    text: o.text,
    mode: 'line',
    constrain: true,
    maxWidth: o.width,
    boxHeight: 0,
    minFont: Math.max(6, o.size - 3),
    maxFont: o.size,
    font: o.size,
    overflow: 'clip',
    hAlign: o.align ?? 'left',
    vAlign: 'top',
    fontFamily: o.family ?? SANS,
    fontWeight: o.weight ?? 400,
    fontStyle: o.italic ? 'italic' : 'normal',
    ...(o.tracking ? { letterSpacing: o.tracking } : {}),
    ...(o.lineHeight ? { lineHeight: o.lineHeight } : {}),
    color: o.color ?? INK,
    strokeEnabled: false,
    strokeColor: '#000000',
    strokeWidth: 0,
    textDirection: 'ltr',
    angle: 0,
    fxShadow: false,
  };
}

type ParaOpts = TextOpts & { height: number };

/** Wrapping paragraph text (mode 'box' with explicit box height). */
function para(o: ParaOpts): SeedLayer {
  return {
    ...text(o),
    mode: 'box',
    boxHeight: o.height,
    lineHeight: o.lineHeight ?? 1.5,
  };
}

/** Multi-line text with explicit \n breaks; box height derived from line count. */
function lines(o: TextOpts & { height?: number }): SeedLayer {
  const count = o.text.split('\n').length;
  const lh = o.lineHeight ?? 1.5;
  return para({ ...o, lineHeight: lh, height: o.height ?? Math.ceil(count * o.size * lh) + 2 });
}

type RectOpts = {
  name: string;
  left: number;
  top: number;
  w: number;
  h: number;
  fill: string;
  radius?: number;
  opacity?: number;
};

function rect(o: RectOpts): SeedLayer {
  return {
    name: o.name,
    type: 'rect',
    x: o.left + o.w / 2 - PAGE_W / 2,
    y: o.top + o.h / 2 - PAGE_H / 2,
    w: o.w,
    h: o.h,
    width: o.w,
    height: o.h,
    fill: o.fill,
    ...(o.radius ? { borderRadius: o.radius } : {}),
    ...(o.opacity != null ? { opacity: o.opacity } : {}),
    angle: 0,
  };
}

type GraphOpts = {
  name: string;
  left: number;
  top: number;
  w: number;
  h: number;
  kind: 'bar' | 'line' | 'area' | 'donut' | 'pie' | 'radial';
  bindings: Array<Record<string, unknown>>;
  options: Record<string, unknown>;
};

function graph(o: GraphOpts): SeedLayer {
  return {
    name: o.name,
    type: 'graph',
    graphKind: o.kind,
    x: o.left + o.w / 2 - PAGE_W / 2,
    y: o.top + o.h / 2 - PAGE_H / 2,
    w: o.w,
    h: o.h,
    width: o.w,
    height: o.h,
    dataBindings: o.bindings,
    options: o.options,
    angle: 0,
  };
}

const solid = (color: string) => ({ type: 'solid', color });

/* ── Composite builders ───────────────────────────────────────────────── */

/** Shared inner-page chrome: top accent bar, header captions, footer rule + page number. */
function pageChrome(o: {
  accent: string;
  docLabel: string;
  footerLeft: string;
  page: number;
  total: number;
}): SeedLayer[] {
  return [
    rect({ name: 'Top Accent Bar', left: 0, top: 0, w: PAGE_W, h: 6, fill: o.accent }),
    text({ name: 'Header Doc Label', left: M, top: 30, width: 460, text: o.docLabel.toUpperCase(), size: 9.5, weight: 700, color: MUTED, tracking: 1.6 }),
    text({ name: 'Header Page Marker', left: PAGE_W - M - 160, top: 30, width: 160, text: `${String(o.page).padStart(2, '0')} / ${String(o.total).padStart(2, '0')}`, size: 9.5, weight: 600, color: FAINT, align: 'right', tracking: 1.2 }),
    rect({ name: 'Footer Rule', left: M, top: PAGE_H - 66, w: CW, h: 1, fill: RULE }),
    text({ name: 'Footer Note', left: M, top: PAGE_H - 54, width: 480, text: o.footerLeft, size: 9, weight: 500, color: FAINT }),
    text({ name: 'Footer Page Number', left: PAGE_W - M - 160, top: PAGE_H - 54, width: 160, text: `Page ${o.page} of ${o.total}`, size: 9, weight: 500, color: FAINT, align: 'right' }),
  ];
}

/** Kicker + page title + optional subtitle + short accent rule. Returns layers; content should start ~44px below the returned bottom. */
function pageTitle(o: { kicker: string; title: string; sub?: string; accent: string; top?: number }): { layers: SeedLayer[]; bottom: number } {
  const top = o.top ?? 66;
  const out: SeedLayer[] = [];
  let y = top;
  out.push(text({ name: 'Section Kicker', left: M, top: y, width: CW, text: o.kicker.toUpperCase(), size: 10, weight: 700, color: o.accent, tracking: 2 }));
  y += 22;
  out.push(text({ name: 'Page Title', left: M, top: y, width: CW, text: o.title, size: 30, weight: 700, color: INK }));
  y += 44;
  if (o.sub) {
    out.push(text({ name: 'Page Subtitle', left: M, top: y, width: CW, text: o.sub, size: 12, weight: 400, color: MUTED }));
    y += 24;
  }
  out.push(rect({ name: 'Title Rule', left: M, top: y + 4, w: 56, h: 3, fill: o.accent }));
  return { layers: out, bottom: y + 7 };
}

/** Small uppercase section header inside a page. */
function sectionLabel(o: { name?: string; label: string; top: number; accent: string; left?: number; width?: number }): SeedLayer[] {
  const left = o.left ?? M;
  return [
    text({ name: o.name ?? `${o.label} Label`, left, top: o.top, width: o.width ?? CW, text: o.label.toUpperCase(), size: 10, weight: 700, color: o.accent, tracking: 1.8 }),
    rect({ name: `${o.name ?? o.label} Label Rule`, left, top: o.top + 18, w: 28, h: 2, fill: o.accent }),
  ];
}

/** Stat/KPI card: quiet panel, accent tick, caption label, large value, optional delta line. */
function kpiCard(o: {
  name: string;
  left: number;
  top: number;
  w: number;
  h?: number;
  label: string;
  value: string;
  delta?: string;
  deltaColor?: string;
  accent: string;
  valueSize?: number;
}): SeedLayer[] {
  const h = o.h ?? 92;
  const out: SeedLayer[] = [
    rect({ name: `${o.name} Card`, left: o.left, top: o.top, w: o.w, h, fill: CARD, radius: 8 }),
    rect({ name: `${o.name} Tick`, left: o.left, top: o.top, w: 3, h, fill: o.accent }),
    text({ name: `${o.name} Label`, left: o.left + 16, top: o.top + 14, width: o.w - 30, text: o.label.toUpperCase(), size: 8.5, weight: 700, color: MUTED, tracking: 1.1 }),
    text({ name: `${o.name} Value`, left: o.left + 16, top: o.top + 32, width: o.w - 30, text: o.value, size: o.valueSize ?? 23, weight: 700, color: INK }),
  ];
  if (o.delta) {
    out.push(text({ name: `${o.name} Delta`, left: o.left + 16, top: o.top + h - 26, width: o.w - 30, text: o.delta, size: 9.5, weight: 600, color: o.deltaColor ?? OK_GREEN }));
  }
  return out;
}

type TableCol = {
  label: string;
  /** Text-box left edge (already padded). */
  left: number;
  /** Text-box width. */
  width: number;
  align?: 'left' | 'right' | 'center';
};

/** Simple ruled table: dark header band, single-line cells, hairline row rules. */
function table(o: {
  name: string;
  top: number;
  cols: TableCol[];
  rows: string[][];
  headFill?: string;
  headColor?: string;
  left?: number;
  width?: number;
  headH?: number;
  rowH?: number;
  rowSize?: number;
  strongCol?: number;
  zebra?: boolean;
}): { layers: SeedLayer[]; bottom: number } {
  const left = o.left ?? M;
  const width = o.width ?? CW;
  const headH = o.headH ?? 30;
  const rowH = o.rowH ?? 34;
  const rowSize = o.rowSize ?? 10.5;
  const out: SeedLayer[] = [];

  out.push(rect({ name: `${o.name} Header Band`, left, top: o.top, w: width, h: headH, fill: o.headFill ?? INK, radius: 4 }));
  for (const col of o.cols) {
    out.push(text({
      name: `${o.name} Header ${col.label}`,
      left: col.left,
      top: o.top + (headH - 9) / 2 - 1,
      width: col.width,
      text: col.label.toUpperCase(),
      size: 8.5,
      weight: 700,
      color: o.headColor ?? '#f9fafb',
      align: col.align ?? 'left',
      tracking: 1,
    }));
  }

  o.rows.forEach((row, i) => {
    const rowTop = o.top + headH + i * rowH;
    if (o.zebra && i % 2 === 1) {
      out.push(rect({ name: `${o.name} Row ${i + 1} Zebra`, left, top: rowTop, w: width, h: rowH, fill: CARD }));
    }
    out.push(rect({ name: `${o.name} Row ${i + 1} Rule`, left, top: rowTop + rowH - 1, w: width, h: 1, fill: RULE }));
    row.forEach((cell, j) => {
      const col = o.cols[j];
      if (!col || !cell) return;
      out.push(text({
        name: `${o.name} R${i + 1} ${col.label}`,
        left: col.left,
        top: rowTop + (rowH - rowSize * 1.2) / 2,
        width: col.width,
        text: cell,
        size: rowSize,
        weight: j === (o.strongCol ?? -1) ? 600 : 400,
        color: j === (o.strongCol ?? -1) ? INK : BODY_COLOR,
        align: col.align ?? 'left',
      }));
    });
  });

  return { layers: out, bottom: o.top + headH + o.rows.length * rowH };
}

/** Signature capture block: party label + ruled lines for signature, name/title, date. */
function signatureBlock(o: { name: string; left: number; top: number; w: number; party: string; accent: string }): SeedLayer[] {
  const out: SeedLayer[] = [
    text({ name: `${o.name} Party`, left: o.left, top: o.top, width: o.w, text: o.party.toUpperCase(), size: 9, weight: 700, color: o.accent, tracking: 1.4 }),
  ];
  const fieldLabels = ['Signature', 'Name & Title', 'Date'];
  fieldLabels.forEach((label, i) => {
    const y = o.top + 52 + i * 54;
    out.push(rect({ name: `${o.name} ${label} Line`, left: o.left, top: y, w: o.w, h: 1, fill: FAINT }));
    out.push(text({ name: `${o.name} ${label} Label`, left: o.left, top: y + 7, width: o.w, text: label, size: 8.5, weight: 500, color: FAINT }));
  });
  return out;
}

/* ── FlowText helpers (long-form legal bodies) ────────────────────────── */

type FlowBlock = {
  id: string;
  type: string;
  runs?: Array<{ text: string; style?: Record<string, unknown> }>;
  align?: string;
};

function fb(seedId: string, nonce: string, suffix: string, type: string, textContent: string): FlowBlock {
  return { id: `ft-${seedId}-${nonce}-${suffix}`, type, runs: [{ text: textContent }] };
}

/**
 * Document-spanning FlowText body used by the contract and NDA packs.
 * Serif body with sans section headings in the template accent color.
 * Margins leave room for the per-page chrome (header captions + footer).
 */
function flowLegalBody(
  seedId: string,
  nonce: string,
  blocks: FlowBlock[],
  opts: { pages: number; accent: string },
): DocumentLayerEntry {
  return {
    id: `doc-flow-${seedId}-${nonce}`,
    type: 'flowText',
    data: {
      id: `flow-${seedId}-${nonce}`,
      type: 'flowText',
      x: 0,
      y: 0,
      blocks,
      margins: { top: FLOW_TOP, right: M, bottom: FLOW_BOTTOM, left: M },
      fontFamily: SERIF,
      fontSize: 12,
      color: '#1f2937',
      lineHeight: 1.6,
      columns: 1,
      columnGap: 24,
      overflow: 'extend',
      autoPageSpan: true,
      startPage: 1,
      endPage: opts.pages,
      drawOrder: 'front',
      styleOverrides: {
        heading1: { fontSize: 27, fontWeight: '700', marginBottom: 8, color: INK, fontFamily: SERIF },
        heading2: { fontSize: 12.5, fontWeight: '700', marginBottom: 7, color: opts.accent, fontFamily: SANS },
        heading3: { fontSize: 12, fontWeight: '600', marginBottom: 6, color: INK, fontFamily: SANS },
        paragraph: { fontSize: 12, lineHeight: 1.62, marginBottom: 11, color: '#1f2937' },
        bulletList: { fontSize: 12, marginBottom: 6, indentPx: 20, color: '#1f2937' },
        numberedList: { fontSize: 12, marginBottom: 6, indentPx: 20, color: '#1f2937' },
        blockquote: { fontSize: 11.5, color: '#4b5563' },
      },
    },
  };
}

/* ── Template seeds ───────────────────────────────────────────────────── */

const NAVY = '#1e3a5f';
const NAVY_TINT = '#e8eef5';
const FOREST = '#1b4d3e';
const AMBER = '#b45309';
const CHARCOAL = '#111827';
const SLATE = '#334155';
const TEAL = '#0f766e';
const BURGUNDY = '#6d1f2c';
const STEEL = '#2c5282';
const DEEP_TEAL = '#115e59';
const TERRACOTTA = '#9a3412';
const PETROL = '#155e75';
const PLUM = '#4a3572';

const TEMPLATE_SEEDS: TemplateSeed[] = [
  /* ════════════════ 1. CLIENT PROPOSAL — deep navy ════════════════ */
  {
    id: 'client-proposal-pack',
    title: 'Client Proposal',
    shortLabel: 'Client Proposal',
    description: 'Five-page polished proposal: executive summary, phased scope, milestone timeline, fixed-fee investment table, and a countersignable acceptance page.',
    icon: 'fas fa-file-contract',
    composerPresetId: 'proposal-seo-growth',
    pages: [
      {
        label: 'Cover',
        ...LETTER_PAGE,
        layers: [
          rect({ name: 'Cover Panel', left: 0, top: 0, w: PAGE_W, h: 332, fill: NAVY }),
          text({ name: 'Studio Wordmark', left: M, top: 56, width: 400, text: 'ATLAS CREATIVE STUDIO', size: 10, weight: 700, color: '#a9c1dd', tracking: 2.2 }),
          text({ name: 'Cover Date', left: PAGE_W - M - 200, top: 56, width: 200, text: 'AUGUST 2026', size: 10, weight: 600, color: '#7f9dbf', align: 'right', tracking: 2 }),
          text({ name: 'Cover Eyebrow', left: M, top: 126, width: 400, text: 'PROJECT PROPOSAL', size: 10.5, weight: 700, color: '#8fb4dc', tracking: 2.6 }),
          text({ name: 'Cover Title Line 1', left: M, top: 152, width: 640, text: 'Website Redesign &', size: 38, weight: 700, color: '#ffffff' }),
          text({ name: 'Cover Title Line 2', left: M, top: 198, width: 640, text: 'Growth Partnership', size: 38, weight: 700, color: '#ffffff' }),
          para({ name: 'Cover Subtitle', left: M, top: 256, width: 560, height: 42, text: 'A conversion-focused rebuild of meridianoutdoor.com — strategy, design, build, and launch in twelve weeks.', size: 12.5, weight: 400, color: '#c6d4e6', lineHeight: 1.55 }),
          rect({ name: 'Prepared For Card', left: M, top: 384, w: 336, h: 132, fill: CARD, radius: 8 }),
          rect({ name: 'Prepared For Tick', left: M, top: 384, w: 3, h: 132, fill: NAVY }),
          text({ name: 'Prepared For Label', left: M + 20, top: 402, width: 296, text: 'PREPARED FOR', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          text({ name: 'Client Name', left: M + 20, top: 424, width: 296, text: 'Meridian Outdoor Co.', size: 13, weight: 600, color: INK }),
          lines({ name: 'Client Contact', left: M + 20, top: 448, width: 296, text: 'Dana Whitfield, VP Marketing\ndana@meridianoutdoor.com', size: 10.5, weight: 400, color: MUTED, lineHeight: 1.6 }),
          rect({ name: 'Prepared By Card', left: 416, top: 384, w: 336, h: 132, fill: CARD, radius: 8 }),
          rect({ name: 'Prepared By Tick', left: 416, top: 384, w: 3, h: 132, fill: NAVY }),
          text({ name: 'Prepared By Label', left: 436, top: 402, width: 296, text: 'PREPARED BY', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          text({ name: 'Studio Name', left: 436, top: 424, width: 296, text: 'Atlas Creative Studio', size: 13, weight: 600, color: INK }),
          lines({ name: 'Studio Contact', left: 436, top: 448, width: 296, text: 'Jordan Reyes, Principal\nhello@atlascreative.co', size: 10.5, weight: 400, color: MUTED, lineHeight: 1.6 }),
          ...sectionLabel({ label: 'Engagement snapshot', top: 564, accent: NAVY }),
          ...kpiCard({ name: 'Snapshot Timeline', left: 64, top: 600, w: 218, h: 96, label: 'Timeline', value: '12 weeks', delta: 'Kickoff August 3, 2026', deltaColor: MUTED, accent: NAVY }),
          ...kpiCard({ name: 'Snapshot Investment', left: 299, top: 600, w: 218, h: 96, label: 'Investment', value: '$42,600', delta: 'Fixed fee, no surprises', deltaColor: MUTED, accent: NAVY }),
          ...kpiCard({ name: 'Snapshot Launch', left: 534, top: 600, w: 218, h: 96, label: 'Launch', value: 'October 23', delta: 'Week 12, go-live runbook', deltaColor: MUTED, accent: NAVY, valueSize: 21 }),
          ...sectionLabel({ label: 'What is inside', top: 736, accent: NAVY }),
          ...[
            { n: '01', t: 'Executive Summary', p: 'Page 2' },
            { n: '02', t: 'Scope of Work', p: 'Page 3' },
            { n: '03', t: 'Timeline & Investment', p: 'Page 4' },
            { n: '04', t: 'Next Steps & Acceptance', p: 'Page 5' },
          ].flatMap((row, i) => {
            const y = 772 + i * 40;
            return [
              text({ name: `TOC ${row.n} Number`, left: M, top: y, width: 30, text: row.n, size: 11, weight: 700, color: NAVY }),
              text({ name: `TOC ${row.n} Title`, left: 104, top: y - 1, width: 400, text: row.t, size: 12, weight: 600, color: INK }),
              text({ name: `TOC ${row.n} Page`, left: PAGE_W - M - 80, top: y, width: 80, text: row.p, size: 10.5, weight: 500, color: MUTED, align: 'right' }),
              ...(i < 3 ? [rect({ name: `TOC ${row.n} Rule`, left: M, top: y + 26, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          rect({ name: 'Cover Footer Rule', left: M, top: 976, w: CW, h: 1, fill: RULE }),
          text({ name: 'Validity Note', left: M, top: 990, width: 300, text: 'This proposal is valid through August 31, 2026.', size: 9.5, weight: 500, color: MUTED }),
          text({ name: 'Cover Contact Line', left: 424, top: 990, width: 328, text: 'atlascreative.co · hello@atlascreative.co · (415) 555-0134', size: 9, weight: 500, color: FAINT, align: 'right' }),
        ],
      },
      {
        label: 'Executive Summary',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: NAVY, docLabel: 'Project Proposal — Meridian Outdoor Co.', footerLeft: 'Atlas Creative Studio · Project Proposal', page: 2, total: 5 }),
          ...pageTitle({ kicker: '01 · Executive Summary', title: 'The opportunity', sub: 'Where meridianoutdoor.com stands today, and what a focused rebuild unlocks.', accent: NAVY }).layers,
          para({ name: 'Summary Paragraph 1', left: M, top: 198, width: CW, height: 60, text: 'Meridian Outdoor attracts more than 84,000 sessions a month, yet only 2.4% of visitors ever reach checkout. Slow templates, a dated design system, and a five-step checkout are costing real revenue — we estimate $480,000 a year in recoverable sales.', size: 12, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          para({ name: 'Summary Paragraph 2', left: M, top: 264, width: CW, height: 42, text: 'This proposal outlines a twelve-week engagement to rebuild the storefront on a faster, conversion-first foundation: clearer paths to purchase, a design system your team can extend, and measurement wired in from day one.', size: 12, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          ...sectionLabel({ label: 'Objectives', top: 336, accent: NAVY }),
          ...kpiCard({ name: 'Objective Conversion', left: 64, top: 372, w: 218, h: 100, label: 'Conversion rate', value: '2.4% → 3.6%', delta: 'Primary success metric', deltaColor: MUTED, accent: NAVY, valueSize: 20 }),
          ...kpiCard({ name: 'Objective Speed', left: 299, top: 372, w: 218, h: 100, label: 'Page load (LCP)', value: '4.9s → 1.9s', delta: 'Measured on top templates', deltaColor: MUTED, accent: NAVY, valueSize: 20 }),
          ...kpiCard({ name: 'Objective Organic', left: 534, top: 372, w: 218, h: 100, label: 'Organic sessions', value: '+40% by Q1', delta: 'Via technical SEO foundation', deltaColor: MUTED, accent: NAVY, valueSize: 20 }),
          ...sectionLabel({ label: 'Why Atlas', top: 508, accent: NAVY }),
          para({ name: 'Why Atlas Point 1', left: M, top: 544, width: CW, height: 38, text: '•   E-commerce is our lane: 31 storefront builds since 2019, with an average +38% conversion lift in the first ninety days.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          para({ name: 'Why Atlas Point 2', left: M, top: 586, width: CW, height: 38, text: '•   Senior team only — the people in your kickoff call are the people doing the work, end to end.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          para({ name: 'Why Atlas Point 3', left: M, top: 628, width: CW, height: 38, text: '•   Fixed fee, weekly demos, and a shared scorecard, so progress stays visible and the budget never drifts.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          rect({ name: 'Quote Card', left: M, top: 700, w: CW, h: 110, fill: NAVY_TINT, radius: 8 }),
          rect({ name: 'Quote Tick', left: M, top: 700, w: 3, h: 110, fill: NAVY }),
          para({ name: 'Client Quote', left: M + 24, top: 718, width: 616, height: 46, text: '“Atlas rebuilt our store in ten weeks and conversion jumped 42% in the first quarter. The most organized team we have ever worked with.”', size: 13, weight: 400, color: '#1f2937', family: SERIF, italic: true, lineHeight: 1.6 }),
          text({ name: 'Quote Attribution', left: M + 24, top: 774, width: 600, text: 'Casey Lombard — Founder, Redwood Cycling Co.', size: 10, weight: 600, color: MUTED }),
        ],
      },
      {
        label: 'Scope of Work',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: NAVY, docLabel: 'Project Proposal — Meridian Outdoor Co.', footerLeft: 'Atlas Creative Studio · Project Proposal', page: 3, total: 5 }),
          ...pageTitle({ kicker: '02 · Scope of Work', title: 'Four phases, one accountable team', sub: 'Every phase ends in a working demo and a decision — nothing moves without your sign-off.', accent: NAVY }).layers,
          ...[
            { n: '01', weeks: 'Weeks 1–2', title: 'Discovery & Strategy', body: 'Stakeholder interviews, analytics and session-recording review, competitor teardown, and a prioritized conversion roadmap agreed with your team.', output: 'Strategy brief & measurement plan' },
            { n: '02', weeks: 'Weeks 3–6', title: 'Design System & UX', body: 'Wireframes for the nine highest-traffic templates, two visual directions, then a full design system with tokens, components, and states.', output: 'Figma library + 9 template designs' },
            { n: '03', weeks: 'Weeks 7–10', title: 'Build & Integrate', body: 'Shopify theme build, ERP inventory sync, sub-two-second page targets, and WCAG 2.2 AA accessibility verified on every template.', output: 'Staging storefront, QA-passed' },
            { n: '04', weeks: 'Weeks 11–12', title: 'Launch & Optimize', body: 'Content migration, redirect map, launch runbook, then two weeks of post-launch tuning against the shared scorecard.', output: 'Live site + 30-day scorecard' },
          ].flatMap((p, i) => {
            const y = 200 + i * 152;
            return [
              rect({ name: `Phase ${p.n} Chip`, left: M, top: y, w: 92, h: 24, fill: NAVY, radius: 12 }),
              text({ name: `Phase ${p.n} Chip Text`, left: M, top: y + 7, width: 92, text: `PHASE ${p.n}`, size: 8.5, weight: 700, color: '#ffffff', align: 'center', tracking: 1.2 }),
              text({ name: `Phase ${p.n} Weeks`, left: M, top: y + 34, width: 100, text: p.weeks, size: 9.5, weight: 600, color: MUTED }),
              text({ name: `Phase ${p.n} Title`, left: 180, top: y + 2, width: 372, text: p.title, size: 15, weight: 700, color: INK }),
              para({ name: `Phase ${p.n} Body`, left: 180, top: y + 28, width: 372, height: 76, text: p.body, size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
              text({ name: `Phase ${p.n} Output Label`, left: 580, top: y + 2, width: 172, text: 'OUTPUT', size: 8.5, weight: 700, color: FAINT, tracking: 1.4 }),
              para({ name: `Phase ${p.n} Output`, left: 580, top: y + 22, width: 172, height: 52, text: p.output, size: 10.5, weight: 600, color: NAVY, lineHeight: 1.5 }),
              ...(i < 3 ? [rect({ name: `Phase ${p.n} Divider`, left: M, top: y + 126, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          rect({ name: 'Out of Scope Card', left: M, top: 812, w: CW, h: 96, fill: CARD, radius: 8 }),
          text({ name: 'Out of Scope Label', left: M + 24, top: 830, width: 400, text: 'NOT IN SCOPE', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          para({ name: 'Out of Scope Body', left: M + 24, top: 850, width: 640, height: 42, text: 'Ongoing paid media management, photography and video production, and net-new copywriting beyond the nine core templates. We are happy to quote any of these separately.', size: 10.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
        ],
      },
      {
        label: 'Timeline & Investment',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: NAVY, docLabel: 'Project Proposal — Meridian Outdoor Co.', footerLeft: 'Atlas Creative Studio · Project Proposal', page: 4, total: 5 }),
          ...pageTitle({ kicker: '03 · Timeline & Investment', title: 'Milestones, meetings, and money', accent: NAVY }).layers,
          ...table({
            name: 'Milestones',
            top: 180,
            cols: [
              { label: 'Week', left: 80, width: 60 },
              { label: 'Milestone', left: 156, width: 270 },
              { label: 'Decision needed', left: 440, width: 170 },
              { label: 'Date', left: 616, width: 120, align: 'right' },
            ],
            rows: [
              ['W1', 'Kickoff & access granted', 'Analytics + store access', 'Aug 3'],
              ['W2', 'Strategy readout', 'Approve roadmap', 'Aug 14'],
              ['W4', 'Wireframe review', 'Choose visual direction', 'Aug 28'],
              ['W6', 'Design system sign-off', 'Approve for build', 'Sep 11'],
              ['W10', 'Staging walkthrough', 'Content freeze', 'Oct 9'],
              ['W12', 'Launch', 'Go / no-go call', 'Oct 23'],
            ],
            headFill: NAVY,
            strongCol: 1,
          }).layers,
          ...sectionLabel({ label: 'Investment', top: 448, accent: NAVY }),
          ...table({
            name: 'Investment',
            top: 484,
            cols: [
              { label: 'Line item', left: 80, width: 300 },
              { label: 'Timing', left: 400, width: 160 },
              { label: 'Fee', left: 616, width: 120, align: 'right' },
            ],
            rows: [
              ['Discovery & strategy', 'Weeks 1–2', '$4,800'],
              ['Design system & UX', 'Weeks 3–6', '$12,400'],
              ['Build & integration', 'Weeks 7–10', '$21,600'],
              ['Launch & 30-day optimization', 'Weeks 11–12', '$3,800'],
            ],
            headFill: NAVY,
            strongCol: 2,
          }).layers,
          rect({ name: 'Total Band', left: M, top: 650, w: CW, h: 40, fill: NAVY, radius: 4 }),
          text({ name: 'Total Label', left: 80, top: 664, width: 300, text: 'TOTAL — FIXED FEE', size: 10, weight: 700, color: '#dbe6f2', tracking: 1.6 }),
          text({ name: 'Total Value', left: 616, top: 661, width: 120, text: '$42,600', size: 15, weight: 700, color: '#ffffff', align: 'right' }),
          ...sectionLabel({ label: 'Payment schedule', top: 726, accent: NAVY }),
          ...[
            '40% ($17,040) on kickoff — August 3',
            '40% ($17,040) on design approval — September 11',
            '20% ($8,520) on launch — October 23',
          ].flatMap((line, i) => [
            rect({ name: `Payment ${i + 1} Bullet`, left: M, top: 767 + i * 26, w: 5, h: 5, fill: NAVY }),
            text({ name: `Payment ${i + 1}`, left: 80, top: 762 + i * 26, width: 600, text: line, size: 11.5, weight: 500, color: BODY_COLOR }),
          ]),
          para({ name: 'Investment Fine Print', left: M, top: 856, width: CW, height: 34, text: 'Fee excludes app subscriptions and third-party licenses. Invoices are due net 15. Scope changes are estimated and approved in writing before any additional work begins.', size: 9.5, weight: 400, color: FAINT, lineHeight: 1.5 }),
        ],
      },
      {
        label: 'Next Steps & Acceptance',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: NAVY, docLabel: 'Project Proposal — Meridian Outdoor Co.', footerLeft: 'Atlas Creative Studio · Project Proposal', page: 5, total: 5 }),
          ...pageTitle({ kicker: '04 · Next Steps', title: 'From yes to kickoff in five days', sub: 'Sign, schedule, and we start — here is exactly what happens after approval.', accent: NAVY }).layers,
          ...[
            { n: '1', title: 'Sign the acceptance below', when: 'Day 0' },
            { n: '2', title: 'Kickoff scheduled and first invoice issued', when: 'Days 1–2' },
            { n: '3', title: 'Access checklist completed together', when: 'Days 3–4' },
            { n: '4', title: 'Discovery workshop — the real work begins', when: 'Day 5' },
          ].flatMap((s, i) => {
            const y = 200 + i * 58;
            return [
              rect({ name: `Step ${s.n} Chip`, left: M, top: y, w: 26, h: 26, fill: NAVY, radius: 13 }),
              text({ name: `Step ${s.n} Number`, left: M, top: y + 6, width: 26, text: s.n, size: 11, weight: 700, color: '#ffffff', align: 'center' }),
              text({ name: `Step ${s.n} Title`, left: 108, top: y + 4, width: 480, text: s.title, size: 12.5, weight: 600, color: INK }),
              text({ name: `Step ${s.n} When`, left: PAGE_W - M - 120, top: y + 6, width: 120, text: s.when, size: 10, weight: 600, color: MUTED, align: 'right' }),
              ...(i < 3 ? [rect({ name: `Step ${s.n} Rule`, left: M, top: y + 42, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          rect({ name: 'Assumptions Card', left: M, top: 452, w: CW, h: 100, fill: CARD, radius: 8 }),
          text({ name: 'Assumptions Label', left: M + 24, top: 470, width: 400, text: 'GOOD TO KNOW', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          para({ name: 'Assumptions Body', left: M + 24, top: 490, width: 640, height: 50, text: 'This proposal is valid through August 31, 2026. The timeline assumes content and product data arrive by week 6 and one consolidated round of feedback per milestone. If reality is messier, we re-plan together before spending your budget.', size: 10.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          ...sectionLabel({ label: 'Acceptance', top: 592, accent: NAVY }),
          text({ name: 'Acceptance Intro', left: M, top: 628, width: CW, text: 'Approved and agreed — this page may be countersigned in place of a separate order form.', size: 11.5, weight: 400, color: BODY_COLOR }),
          ...signatureBlock({ name: 'Client Signature', left: M, top: 672, w: 328, party: 'Meridian Outdoor Co.', accent: NAVY }),
          ...signatureBlock({ name: 'Studio Signature', left: 424, top: 672, w: 328, party: 'Atlas Creative Studio', accent: NAVY }),
        ],
      },
    ],
  },

  /* ════════════════ 2. SERVICE CONTRACT — forest, FlowText body ════════════════ */
  {
    id: 'service-contract-pack',
    title: 'Service Agreement',
    shortLabel: 'Service Contract',
    description: 'Four-page professional service agreement with real clause structure — services, fees, IP, confidentiality, liability, termination — flowing to a signature page.',
    icon: 'fas fa-file-signature',
    composerPresetId: 'contract-retainer-basic',
    buildDocumentLayers: (nonce: string) => {
      const id = 'service-contract-pack';
      const b = (suffix: string, type: string, t: string) => fb(id, nonce, suffix, type, t);
      return [flowLegalBody(id, nonce, [
        b('h1', 'heading1', 'Professional Services Agreement'),
        b('intro', 'paragraph', 'This Professional Services Agreement (the “Agreement”) is entered into as of September 1, 2026 (the “Effective Date”) by and between Atlas Creative Studio LLC, a California limited liability company with offices at 548 Market Street, Suite 210, San Francisco, CA 94104 (“Provider”), and Harbor & Pine Realty Group, Inc., a California corporation with offices at 1220 Coastal Highway, Half Moon Bay, CA 94019 (“Client”). Each may be referred to as a “Party” and together as the “Parties.”'),
        b('s1t', 'heading2', '1.  SERVICES'),
        b('s1a', 'paragraph', 'Provider will perform the design, development, and marketing services described in one or more mutually executed Statements of Work (each, a “SOW”). Each SOW will identify the scope, deliverables, schedule, fees, and any assumptions specific to that engagement. The initial engagement consists of a monthly marketing retainer covering:'),
        b('s1b', 'bulletList', 'Search engine optimization: technical remediation, content briefs, and internal-link planning'),
        b('s1c', 'bulletList', 'Listing and landing-page design updates, up to eight templates per quarter'),
        b('s1d', 'bulletList', 'Monthly performance reporting with a written summary and prioritized recommendations'),
        b('s1e', 'bulletList', 'One strategy session per month with Client stakeholders'),
        b('s2t', 'heading2', '2.  TERM AND RENEWAL'),
        b('s2a', 'paragraph', 'This Agreement begins on the Effective Date and continues for an initial term of six (6) months. Thereafter it renews automatically for successive one-month periods unless either Party gives at least thirty (30) days written notice of non-renewal. Any provision that by its nature should survive — including confidentiality, intellectual property, limitation of liability, and payment obligations — survives expiration or termination.'),
        b('s3t', 'heading2', '3.  FEES AND RETAINER'),
        b('s3a', 'paragraph', 'Client will pay Provider a monthly retainer of $4,250, invoiced on the first business day of each month. In addition:'),
        b('s3b', 'bulletList', 'A one-time onboarding fee of $1,500 is due with the first invoice'),
        b('s3c', 'bulletList', 'Approved work beyond the retainer scope is billed at $150 per hour in quarter-hour increments'),
        b('s3d', 'bulletList', 'Rates are fixed for the initial term and may be adjusted at renewal with thirty (30) days notice'),
        b('s4t', 'heading2', '4.  EXPENSES'),
        b('s4a', 'paragraph', 'Pre-approved, documented out-of-pocket expenses (stock licensing, premium plugins, paid research panels, travel requested by Client) are billed at cost with no markup. Any single expense over $250 requires Client approval in writing before it is incurred.'),
        b('s5t', 'heading2', '5.  INVOICING AND LATE PAYMENT'),
        b('s5a', 'paragraph', 'Invoices are due net fifteen (15) days from the invoice date. Balances more than five (5) days past due accrue interest at 1.5% per month or the maximum rate permitted by law, whichever is lower. Provider may pause work on any account more than fifteen (15) days past due after written notice, and the schedule extends day-for-day with any pause.'),
        b('s6t', 'heading2', '6.  CHANGE ORDERS'),
        b('s6a', 'paragraph', 'Either Party may propose a change to scope, schedule, or fees. Changes take effect only after both Parties sign a written change order describing the change and its impact. Provider will not perform out-of-scope work — and Client will not be billed for it — without a signed change order.'),
        b('s7t', 'heading2', '7.  CLIENT RESPONSIBILITIES'),
        b('s7a', 'paragraph', 'Timely collaboration keeps the schedule honest. Client agrees to:'),
        b('s7b', 'bulletList', 'Designate one authorized decision-maker for approvals and consolidated feedback'),
        b('s7c', 'bulletList', 'Provide brand assets, content, and system access identified in the kickoff checklist'),
        b('s7d', 'bulletList', 'Respond to review requests within five (5) business days'),
        b('s7e', 'bulletList', 'Ensure it holds rights to all materials supplied to Provider'),
        b('s7f', 'bulletList', 'Keep billing and contact information current with Provider’s accounts team'),
        b('s8bt', 'heading2', '8.  SERVICE LEVELS AND MEETINGS'),
        b('s8ba', 'paragraph', 'Provider maintains the following operating rhythm for retainer work, measured monthly and reported in the shared scorecard:'),
        b('s8bb', 'bulletList', 'Business-hours response inside four (4) working hours in the shared channel'),
        b('s8bc', 'bulletList', 'A weekly 25-minute working session with a written summary the same day'),
        b('s8bd', 'bulletList', 'A monthly performance report delivered by the first Friday of the following month'),
        b('s8t', 'heading2', '9.  INTELLECTUAL PROPERTY'),
        b('s8a', 'paragraph', 'Upon full payment of all fees due under the applicable SOW, Provider assigns to Client all right, title, and interest in the final deliverables, excluding Provider Tools. “Provider Tools” means pre-existing frameworks, component libraries, processes, and know-how used to build the deliverables; Provider grants Client a perpetual, non-exclusive, royalty-free license to use Provider Tools as embedded in the deliverables. Third-party assets (fonts, stock, plugins) remain subject to their own licenses, which Provider will identify in the handoff documentation.'),
        b('s9t', 'heading2', '10.  PORTFOLIO USE'),
        b('s9a', 'paragraph', 'Provider may display the finished, publicly released work in its portfolio and case studies, and may reference Client by name and logo, unless Client withdraws consent in writing. No non-public metrics will be published without Client approval.'),
        b('s10t', 'heading2', '11.  CONFIDENTIALITY'),
        b('s10a', 'paragraph', 'Each Party will protect the other’s non-public business, technical, and financial information with at least the care it uses for its own confidential information, and never less than reasonable care. Confidential information may be used only to perform under this Agreement and shared only with personnel and subcontractors bound by obligations at least as protective. These obligations continue for three (3) years after termination and do not apply to information that is public through no fault of the recipient, already known, independently developed, or lawfully received from a third party.'),
        b('s11bt', 'heading2', '12.  DATA PROTECTION'),
        b('s11ba', 'paragraph', 'To the extent Provider processes personal data of Client’s customers, it does so only on Client’s documented instructions, applies commercially reasonable technical and organizational safeguards, and notifies Client without undue delay of any confirmed personal-data breach affecting Client data.'),
        b('s11t', 'heading2', '13.  SUBCONTRACTORS'),
        b('s11a', 'paragraph', 'Provider may engage qualified subcontractors to perform portions of the services, remains fully responsible for their work, and ensures they are bound by confidentiality and IP terms consistent with this Agreement.'),
        b('s12t', 'heading2', '14.  WARRANTIES AND DISCLAIMER'),
        b('s12a', 'paragraph', 'Provider warrants that services will be performed in a professional and workmanlike manner consistent with industry standards, and that deliverables will materially conform to the applicable SOW for thirty (30) days after acceptance. Provider’s sole obligation for breach of this warranty is re-performance of the non-conforming work. EXCEPT AS STATED ABOVE, ALL SERVICES AND DELIVERABLES ARE PROVIDED “AS IS” AND PROVIDER DISCLAIMS ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. PROVIDER DOES NOT WARRANT SPECIFIC MARKETING OUTCOMES, RANKINGS, OR REVENUE RESULTS.'),
        b('s13t', 'heading2', '15.  INDEMNIFICATION'),
        b('s13a', 'paragraph', 'Each Party will defend and indemnify the other against third-party claims arising from its breach of this Agreement, its negligence or willful misconduct, or, in Client’s case, materials Client supplied and, in Provider’s case, allegations that unmodified deliverables infringe a third party’s intellectual-property rights. The indemnified Party must give prompt notice and reasonable cooperation, and the indemnifying Party controls the defense.'),
        b('s14t', 'heading2', '16.  LIMITATION OF LIABILITY'),
        b('s14a', 'paragraph', 'NEITHER PARTY IS LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA, EVEN IF ADVISED OF THE POSSIBILITY. EACH PARTY’S TOTAL AGGREGATE LIABILITY UNDER THIS AGREEMENT IS CAPPED AT THE FEES PAID OR PAYABLE TO PROVIDER IN THE SIX (6) MONTHS PRECEDING THE CLAIM. THIS CAP DOES NOT APPLY TO BREACHES OF SECTION 11, INDEMNIFICATION OBLIGATIONS, OR CLIENT’S PAYMENT OBLIGATIONS.'),
        b('s14bt', 'heading2', '17.  INSURANCE'),
        b('s14ba', 'paragraph', 'Provider maintains commercially reasonable insurance for a studio of its size, including general liability and professional liability (errors and omissions) coverage, and will furnish certificates of insurance on written request.'),
        b('s15t', 'heading2', '18.  TERMINATION'),
        b('s15a', 'paragraph', 'Either Party may terminate this Agreement or any SOW (a) for convenience on thirty (30) days written notice, or (b) immediately if the other Party materially breaches and fails to cure within fifteen (15) days of written notice. On termination, Client pays for all work performed and non-cancelable commitments made through the effective date, and Provider delivers work-in-progress in its then-current form once the account is settled.'),
        b('s16t', 'heading2', '19.  NON-SOLICITATION'),
        b('s16a', 'paragraph', 'During the term and for twelve (12) months after, neither Party will directly solicit for employment the other Party’s personnel who worked on the engagement, except through general job postings not targeted at such personnel. If a Party hires the other’s team member in violation of this section, it will pay a placement fee equal to thirty percent (30%) of the individual’s first-year compensation.'),
        b('s16bt', 'heading2', '20.  FORCE MAJEURE'),
        b('s16ba', 'paragraph', 'Neither Party is liable for delay or failure to perform (other than payment obligations) caused by events beyond its reasonable control — including natural disasters, labor disputes, utility or platform outages, and acts of government — provided the affected Party gives prompt notice and resumes performance as soon as reasonably possible.'),
        b('s16ct', 'heading2', '21.  NOTICES'),
        b('s16ca', 'paragraph', 'Formal notices under this Agreement must be in writing and are deemed given when delivered by hand or courier, or when sent by email to the addresses on the first page with confirmation of transmission, in each case addressed to the signatory below.'),
        b('s17t', 'heading2', '22.  GENERAL'),
        b('s17a', 'paragraph', 'This Agreement is governed by the laws of the State of California, with exclusive venue in the state and federal courts of San Francisco County. It is the entire agreement between the Parties on its subject, supersedes prior discussions, and may be amended only in a writing signed by both Parties. Neither Party may assign this Agreement without the other’s consent, except to a successor in a merger or asset sale. If any provision is held unenforceable, it will be modified to the minimum extent necessary and the remainder stays in effect. A waiver of one breach is not a waiver of any other.'),
        b('sig-div', 'divider', ''),
        b('sig-t', 'heading2', 'SIGNATURES'),
        b('sig-lead', 'paragraph', 'IN WITNESS WHEREOF, the Parties have executed this Agreement as of the Effective Date, each signature below made by a duly authorized representative.'),
        b('sig-p-t', 'heading3', 'PROVIDER — ATLAS CREATIVE STUDIO LLC'),
        b('sig-p-1', 'paragraph', 'Signature:  ______________________________________'),
        b('sig-p-2', 'paragraph', 'Name / Title:  Jordan Reyes, Principal'),
        b('sig-p-3', 'paragraph', 'Date:  ______________________'),
        b('sig-c-t', 'heading3', 'CLIENT — HARBOR & PINE REALTY GROUP, INC.'),
        b('sig-c-1', 'paragraph', 'Signature:  ______________________________________'),
        b('sig-c-2', 'paragraph', 'Name / Title:  ______________________________________'),
        b('sig-c-3', 'paragraph', 'Date:  ______________________'),
      ], { pages: 4, accent: FOREST })];
    },
    pages: [1, 2, 3, 4].map((n) => ({
      label: n === 1 ? 'Agreement' : n === 4 ? 'Execution' : `Terms (${n})`,
      ...LETTER_PAGE,
      layers: [
        ...pageChrome({ accent: FOREST, docLabel: 'Professional Services Agreement', footerLeft: 'Atlas Creative Studio · Services Agreement — Harbor & Pine Realty', page: n, total: 4 }),
        ...(n === 1
          ? [text({ name: 'Confidential Tag', left: PAGE_W - M - 300, top: 46, width: 300, text: 'Confidential — for the named parties only', size: 8.5, weight: 500, color: FAINT, align: 'right' })]
          : []),
      ],
    })),
  },

  /* ════════════════ 3. SERVICE INVOICE — charcoal + amber ════════════════ */
  {
    id: 'service-invoice-pack',
    title: 'Service Invoice',
    shortLabel: 'Service Invoice',
    description: 'Three-page invoice packet: itemized invoice with accurate totals, payment options and terms, and a client-ready statement of delivered work.',
    icon: 'fas fa-file-invoice-dollar',
    composerPresetId: 'invoice-service-statement',
    pages: [
      {
        label: 'Invoice',
        ...LETTER_PAGE,
        layers: [
          rect({ name: 'Top Accent Bar', left: 0, top: 0, w: PAGE_W, h: 6, fill: CHARCOAL }),
          text({ name: 'Studio Name', left: M, top: 48, width: 380, text: 'Atlas Creative Studio', size: 15, weight: 700, color: INK }),
          lines({ name: 'Studio Address', left: M, top: 76, width: 380, text: '548 Market Street, Suite 210\nSan Francisco, CA 94104\nbilling@atlascreative.co · (415) 555-0134', size: 9.5, weight: 400, color: MUTED, lineHeight: 1.6 }),
          text({ name: 'Invoice Title', left: 492, top: 44, width: 260, text: 'INVOICE', size: 30, weight: 800, color: INK, align: 'right', tracking: 2 }),
          rect({ name: 'Invoice Tick', left: 688, top: 84, w: 64, h: 3, fill: AMBER }),
          text({ name: 'Invoice Number', left: 492, top: 98, width: 260, text: 'INV-2041', size: 13, weight: 700, color: AMBER, align: 'right' }),
          lines({ name: 'Invoice Dates', left: 492, top: 122, width: 260, text: 'Issued — June 30, 2026\nDue — July 15, 2026 · Net 15', size: 10, weight: 500, color: MUTED, align: 'right', lineHeight: 1.7 }),
          rect({ name: 'Header Rule', left: M, top: 182, w: CW, h: 1, fill: RULE }),
          rect({ name: 'Bill To Card', left: M, top: 208, w: 336, h: 130, fill: CARD, radius: 8 }),
          rect({ name: 'Bill To Tick', left: M, top: 208, w: 3, h: 130, fill: AMBER }),
          text({ name: 'Bill To Label', left: M + 20, top: 226, width: 296, text: 'BILL TO', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          text({ name: 'Client Name', left: M + 20, top: 246, width: 296, text: 'Bluestone Kitchen + Bar', size: 13, weight: 600, color: INK }),
          lines({ name: 'Client Address', left: M + 20, top: 270, width: 296, text: 'Micah Turner, Owner\n77 Water Street\nPortland, ME 04101', size: 10, weight: 400, color: MUTED, lineHeight: 1.6 }),
          rect({ name: 'Project Card', left: 416, top: 208, w: 336, h: 130, fill: CARD, radius: 8 }),
          rect({ name: 'Project Tick', left: 416, top: 208, w: 3, h: 130, fill: AMBER }),
          text({ name: 'Project Label', left: 436, top: 226, width: 296, text: 'PROJECT', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          text({ name: 'Project Name', left: 436, top: 246, width: 296, text: 'Website redesign & launch', size: 13, weight: 600, color: INK }),
          lines({ name: 'Project Meta', left: 436, top: 270, width: 296, text: 'PO — BK-2214\nBilling period — June 2026\nPay by ACH, card, or check', size: 10, weight: 400, color: MUTED, lineHeight: 1.6 }),
          ...table({
            name: 'Line Items',
            top: 380,
            cols: [
              { label: 'Description', left: 80, width: 320 },
              { label: 'Qty / hrs', left: 420, width: 70, align: 'right' },
              { label: 'Rate', left: 510, width: 90, align: 'right' },
              { label: 'Amount', left: 616, width: 120, align: 'right' },
            ],
            rows: [
              ['Website design — UX & UI, 12 templates', '62.0', '$145.00', '$8,990.00'],
              ['Storefront development & CMS setup', '48.0', '$145.00', '$6,960.00'],
              ['Copywriting — 8 core pages', 'flat', '—', '$2,400.00'],
              ['On-page SEO & analytics setup', 'flat', '—', '$1,150.00'],
              ['Project management — June', 'flat', '—', '$850.00'],
            ],
            headFill: CHARCOAL,
            rowH: 38,
            strongCol: 0,
          }).layers,
          para({ name: 'Payment Reference Note', left: M, top: 620, width: 320, height: 90, text: 'Thank you! Please reference INV-2041 on your payment. Questions about this invoice? Email billing@atlascreative.co and we respond within one business day.', size: 10, weight: 400, color: MUTED, lineHeight: 1.6 }),
          text({ name: 'Subtotal Label', left: 456, top: 616, width: 160, text: 'Subtotal', size: 10.5, weight: 500, color: BODY_COLOR, align: 'right' }),
          text({ name: 'Subtotal Value', left: 632, top: 616, width: 120, text: '$20,350.00', size: 10.5, weight: 600, color: INK, align: 'right' }),
          text({ name: 'Discount Label', left: 456, top: 640, width: 160, text: 'Returning-client discount', size: 10.5, weight: 500, color: BODY_COLOR, align: 'right' }),
          text({ name: 'Discount Value', left: 632, top: 640, width: 120, text: '−$1,000.00', size: 10.5, weight: 600, color: OK_GREEN, align: 'right' }),
          text({ name: 'Tax Label', left: 456, top: 664, width: 160, text: 'Sales tax (8.25%)', size: 10.5, weight: 500, color: BODY_COLOR, align: 'right' }),
          text({ name: 'Tax Value', left: 632, top: 664, width: 120, text: '$1,596.38', size: 10.5, weight: 600, color: INK, align: 'right' }),
          text({ name: 'Deposit Label', left: 456, top: 688, width: 160, text: 'Deposit applied (INV-2028)', size: 10.5, weight: 500, color: BODY_COLOR, align: 'right' }),
          text({ name: 'Deposit Value', left: 632, top: 688, width: 120, text: '−$6,000.00', size: 10.5, weight: 600, color: OK_GREEN, align: 'right' }),
          rect({ name: 'Totals Rule', left: 456, top: 714, w: 296, h: 1, fill: RULE }),
          rect({ name: 'Balance Band', left: 456, top: 726, w: 296, h: 42, fill: AMBER, radius: 4 }),
          text({ name: 'Balance Label', left: 472, top: 741, width: 130, text: 'BALANCE DUE', size: 9.5, weight: 700, color: '#fef3e2', tracking: 1.4 }),
          text({ name: 'Balance Value', left: 616, top: 737, width: 120, text: '$14,946.38', size: 15, weight: 700, color: '#ffffff', align: 'right' }),
          ...sectionLabel({ label: 'Pay it your way', top: 800, accent: AMBER }),
          rect({ name: 'ACH Mini Card', left: 64, top: 836, w: 218, h: 92, fill: CARD, radius: 8 }),
          text({ name: 'ACH Mini Label', left: 80, top: 852, width: 186, text: 'ACH — NO FEE', size: 8.5, weight: 700, color: AMBER, tracking: 1.2 }),
          lines({ name: 'ACH Mini Detail', left: 80, top: 872, width: 186, text: 'Mercury Bank\nRouting 121000358\nAccount ···· 4417', size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.55 }),
          rect({ name: 'Card Mini Card', left: 299, top: 836, w: 218, h: 92, fill: CARD, radius: 8 }),
          text({ name: 'Card Mini Label', left: 315, top: 852, width: 186, text: 'CARD', size: 8.5, weight: 700, color: AMBER, tracking: 1.2 }),
          lines({ name: 'Card Mini Detail', left: 315, top: 872, width: 186, text: 'pay.atlascreative.co/INV-2041\nVisa · MC · Amex\n2.9% processing fee', size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.55 }),
          rect({ name: 'Check Mini Card', left: 534, top: 836, w: 218, h: 92, fill: CARD, radius: 8 }),
          text({ name: 'Check Mini Label', left: 550, top: 852, width: 186, text: 'CHECK', size: 8.5, weight: 700, color: AMBER, tracking: 1.2 }),
          lines({ name: 'Check Mini Detail', left: 550, top: 872, width: 186, text: 'Atlas Creative Studio LLC\n548 Market St, Suite 210\nSan Francisco, CA 94104', size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.55 }),
          rect({ name: 'Footer Rule', left: M, top: PAGE_H - 66, w: CW, h: 1, fill: RULE }),
          text({ name: 'Footer Note', left: M, top: PAGE_H - 54, width: 480, text: 'Invoice INV-2041 · Bluestone Kitchen + Bar', size: 9, weight: 500, color: FAINT }),
          text({ name: 'Footer Page Number', left: PAGE_W - M - 160, top: PAGE_H - 54, width: 160, text: 'Page 1 of 3', size: 9, weight: 500, color: FAINT, align: 'right' }),
        ],
      },
      {
        label: 'Payment & Terms',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: AMBER, docLabel: 'Invoice INV-2041 — Bluestone Kitchen + Bar', footerLeft: 'Atlas Creative Studio · Invoice INV-2041', page: 2, total: 3 }),
          ...pageTitle({ kicker: 'Payment', title: 'How to pay, and the fine print', sub: 'Three ways to settle the balance — plus the terms this invoice is issued under.', accent: AMBER }).layers,
          rect({ name: 'ACH Card', left: 64, top: 200, w: 218, h: 158, fill: CARD, radius: 8 }),
          rect({ name: 'ACH Tick', left: 64, top: 200, w: 3, h: 158, fill: AMBER }),
          text({ name: 'ACH Label', left: 84, top: 218, width: 182, text: 'ACH — PREFERRED', size: 8.5, weight: 700, color: AMBER, tracking: 1.2 }),
          lines({ name: 'ACH Details', left: 84, top: 240, width: 182, text: 'Mercury Bank\nAtlas Creative Studio LLC\nRouting — 121000358\nAccount — ···· 4417\nNo processing fee', size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.7 }),
          rect({ name: 'Card Card', left: 299, top: 200, w: 218, h: 158, fill: CARD, radius: 8 }),
          rect({ name: 'Card Tick', left: 299, top: 200, w: 3, h: 158, fill: AMBER }),
          text({ name: 'Card Label', left: 319, top: 218, width: 182, text: 'CARD', size: 8.5, weight: 700, color: AMBER, tracking: 1.2 }),
          lines({ name: 'Card Details', left: 319, top: 240, width: 182, text: 'pay.atlascreative.co/INV-2041\nVisa, Mastercard, Amex\nReceipt emailed instantly\n2.9% processing fee\napplies at checkout', size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.7 }),
          rect({ name: 'Check Card', left: 534, top: 200, w: 218, h: 158, fill: CARD, radius: 8 }),
          rect({ name: 'Check Tick', left: 534, top: 200, w: 3, h: 158, fill: AMBER }),
          text({ name: 'Check Label', left: 554, top: 218, width: 182, text: 'CHECK', size: 8.5, weight: 700, color: AMBER, tracking: 1.2 }),
          lines({ name: 'Check Details', left: 554, top: 240, width: 182, text: 'Payable to\nAtlas Creative Studio LLC\n548 Market Street, Suite 210\nSan Francisco, CA 94104\nAllow 3–5 days for mail', size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.7 }),
          ...sectionLabel({ label: 'Terms', top: 404, accent: AMBER }),
          ...[
            'Payment is due July 15, 2026 — net 15 from the issue date.',
            'Late balances accrue 1.5% per month after a five-day grace period.',
            'ACH and check payments are fee-free; card payments add a 2.9% processing fee.',
            'Billing questions or disputes should be raised within ten days of the invoice date.',
          ].flatMap((line, i) => [
            rect({ name: `Term ${i + 1} Bullet`, left: M, top: 445 + i * 26, w: 5, h: 5, fill: AMBER }),
            text({ name: `Term ${i + 1}`, left: 80, top: 440 + i * 26, width: 620, text: line, size: 11, weight: 500, color: BODY_COLOR }),
          ]),
          rect({ name: 'Remit Card', left: 64, top: 570, w: 336, h: 118, fill: CARD, radius: 8 }),
          text({ name: 'Remit Label', left: 84, top: 588, width: 296, text: 'PAPERWORK', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          lines({ name: 'Remit Details', left: 84, top: 610, width: 296, text: 'EIN — 88-1402276\nW-9 and vendor forms available on request\nRemittance advice — billing@atlascreative.co', size: 10, weight: 400, color: BODY_COLOR, lineHeight: 1.7 }),
          rect({ name: 'Next Up Card', left: 416, top: 570, w: 336, h: 118, fill: '#fdf3e7', radius: 8 }),
          text({ name: 'Next Up Label', left: 436, top: 588, width: 296, text: 'WHAT’S NEXT', size: 8.5, weight: 700, color: AMBER, tracking: 1.4 }),
          para({ name: 'Next Up Details', left: 436, top: 610, width: 296, height: 60, text: 'The July retainer invoice ($4,250) lands July 31. Post-launch care wraps October 31 — we will send a renewal summary two weeks before.', size: 10, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          text({ name: 'Thanks Note', left: M, top: 736, width: 400, text: 'With thanks,', size: 12, weight: 400, color: BODY_COLOR, family: SERIF, italic: true }),
          text({ name: 'Signatory', left: M, top: 762, width: 480, text: 'Jordan Reyes — Principal, Atlas Creative Studio', size: 11, weight: 600, color: INK }),
        ],
      },
      {
        label: 'Statement of Work Delivered',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: AMBER, docLabel: 'Invoice INV-2041 — Bluestone Kitchen + Bar', footerLeft: 'Atlas Creative Studio · Invoice INV-2041', page: 3, total: 3 }),
          ...pageTitle({ kicker: 'Statement', title: 'What we shipped in June', sub: 'Every hour on invoice INV-2041, itemized by date and lead.', accent: AMBER }).layers,
          ...table({
            name: 'Activity',
            top: 180,
            cols: [
              { label: 'Date', left: 80, width: 70 },
              { label: 'Activity', left: 170, width: 340 },
              { label: 'Hours', left: 530, width: 70, align: 'right' },
              { label: 'Lead', left: 632, width: 120, align: 'right' },
            ],
            rows: [
              ['Jun 2', 'Kickoff workshop & goal alignment', '3.0', 'Jordan'],
              ['Jun 4', 'Analytics + heatmap audit', '6.5', 'Priya'],
              ['Jun 9', 'Sitemap & information architecture', '4.0', 'Jordan'],
              ['Jun 12', 'Wireframes — PDP, cart, checkout', '11.5', 'Sasha'],
              ['Jun 16', 'Wireframe revisions & sign-off', '5.0', 'Sasha'],
              ['Jun 18', 'Visual direction boards', '9.0', 'Sasha'],
              ['Jun 22', 'Design system foundations', '12.0', 'Sasha'],
              ['Jun 25', 'Homepage & PDP high fidelity', '11.0', 'Sasha'],
              ['Jun 26', 'Theme scaffold & CI setup', '14.0', 'Priya'],
              ['Jun 27', 'Product & collection page build', '16.0', 'Priya'],
              ['Jun 29', 'Checkout & payments integration', '13.0', 'Priya'],
              ['Jun 30', 'QA pass + June client demo', '5.0', 'Team'],
            ],
            headFill: CHARCOAL,
            rowH: 32,
            rowSize: 10,
          }).layers,
          rect({ name: 'Hours Total Band', left: M, top: 600, w: CW, h: 38, fill: CHARCOAL, radius: 4 }),
          text({ name: 'Hours Total Label', left: 80, top: 613, width: 300, text: 'JUNE TOTAL', size: 9.5, weight: 700, color: '#d1d5db', tracking: 1.6 }),
          text({ name: 'Hours Total Value', left: 616, top: 610, width: 120, text: '110.0 hours', size: 13, weight: 700, color: '#ffffff', align: 'right' }),
          ...kpiCard({ name: 'Stat Hours', left: 64, top: 672, w: 218, h: 92, label: 'Hours billed', value: '110.0', delta: '62 design · 48 build', deltaColor: MUTED, accent: AMBER }),
          ...kpiCard({ name: 'Stat Deliverables', left: 299, top: 672, w: 218, h: 92, label: 'Deliverables shipped', value: '9', delta: 'All accepted in demo', deltaColor: MUTED, accent: AMBER }),
          ...kpiCard({ name: 'Stat Next', left: 534, top: 672, w: 218, h: 92, label: 'Next invoice', value: 'July 31', delta: 'Monthly retainer — $4,250', deltaColor: MUTED, accent: AMBER, valueSize: 21 }),
          para({ name: 'Statement Note', left: M, top: 800, width: CW, height: 34, text: 'Detailed time entries live in your client portal at portal.atlascreative.co/bluestone. Hours are rounded to the nearest half hour; rounding always favors the client.', size: 9.5, weight: 400, color: FAINT, lineHeight: 1.5 }),
        ],
      },
    ],
  },

  /* ════════════════ 4. MONTHLY REPORT — slate + teal ════════════════ */
  {
    id: 'monthly-report-pack',
    title: 'Monthly Performance Report',
    shortLabel: 'Monthly Report',
    description: 'Five-page client report with KPI cards, real charts (sessions, channel mix, search trend), correct-math tables, and a prioritized recommendations page.',
    icon: 'fas fa-chart-line',
    composerPresetId: 'report-monthly-performance',
    pages: [
      {
        label: 'Cover & KPIs',
        ...LETTER_PAGE,
        layers: [
          rect({ name: 'Cover Panel', left: 0, top: 0, w: PAGE_W, h: 300, fill: SLATE }),
          text({ name: 'Studio Wordmark', left: M, top: 52, width: 400, text: 'ATLAS CREATIVE STUDIO', size: 10, weight: 700, color: '#b3bfd0', tracking: 2.2 }),
          text({ name: 'Cover Date', left: PAGE_W - M - 200, top: 52, width: 200, text: 'JUNE 2026', size: 10, weight: 600, color: '#94a3b8', align: 'right', tracking: 2 }),
          text({ name: 'Cover Eyebrow', left: M, top: 118, width: 480, text: 'MONTHLY PERFORMANCE REPORT', size: 10.5, weight: 700, color: '#7edcd0', tracking: 2.4 }),
          text({ name: 'Cover Title', left: M, top: 142, width: 640, text: 'Summit Physical Therapy', size: 34, weight: 700, color: '#ffffff' }),
          text({ name: 'Cover Subtitle', left: M, top: 196, width: 620, text: 'Digital marketing performance for June 2026, with the plan for July.', size: 12.5, weight: 400, color: '#cbd5e1' }),
          ...kpiCard({ name: 'KPI Sessions', left: 64, top: 340, w: 163, h: 100, label: 'Sessions', value: '14,210', delta: '+18.2% vs May', accent: TEAL, valueSize: 21 }),
          ...kpiCard({ name: 'KPI Leads', left: 239, top: 340, w: 163, h: 100, label: 'Qualified leads', value: '386', delta: '+24.1% vs May', accent: TEAL, valueSize: 21 }),
          ...kpiCard({ name: 'KPI Conversion', left: 414, top: 340, w: 163, h: 100, label: 'Conversion rate', value: '2.72%', delta: '+0.4 pts vs May', accent: TEAL, valueSize: 21 }),
          ...kpiCard({ name: 'KPI CPL', left: 589, top: 340, w: 163, h: 100, label: 'Cost per lead', value: '$38.20', delta: '−12.4% vs May', accent: TEAL, valueSize: 21 }),
          rect({ name: 'Headline Card', left: M, top: 480, w: CW, h: 96, fill: '#e6f2f0', radius: 8 }),
          rect({ name: 'Headline Tick', left: M, top: 480, w: 3, h: 96, fill: TEAL }),
          text({ name: 'Headline Label', left: M + 24, top: 498, width: 400, text: 'HEADLINE', size: 8.5, weight: 700, color: TEAL, tracking: 1.4 }),
          para({ name: 'Headline Body', left: M + 24, top: 518, width: 640, height: 44, text: 'The paid-search restructure cut wasted spend 22% while lead volume grew — June set a twelve-month high for qualified leads at the same budget.', size: 11.5, weight: 500, color: '#134e4a', lineHeight: 1.55 }),
          ...sectionLabel({ label: 'In this report', top: 616, accent: TEAL }),
          ...[
            { n: '01', t: 'Traffic & Acquisition', p: 'Page 2' },
            { n: '02', t: 'Channel Mix', p: 'Page 3' },
            { n: '03', t: 'Organic Search', p: 'Page 4' },
            { n: '04', t: 'Recommendations & July Targets', p: 'Page 5' },
          ].flatMap((row, i) => {
            const y = 652 + i * 40;
            return [
              text({ name: `Report TOC ${row.n}`, left: M, top: y, width: 30, text: row.n, size: 11, weight: 700, color: TEAL }),
              text({ name: `Report TOC ${row.n} Title`, left: 104, top: y - 1, width: 420, text: row.t, size: 12, weight: 600, color: INK }),
              text({ name: `Report TOC ${row.n} Page`, left: PAGE_W - M - 80, top: y, width: 80, text: row.p, size: 10.5, weight: 500, color: MUTED, align: 'right' }),
              ...(i < 3 ? [rect({ name: `Report TOC ${row.n} Rule`, left: M, top: y + 26, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          rect({ name: 'Prepared Card', left: M, top: 836, w: 336, h: 104, fill: CARD, radius: 8 }),
          text({ name: 'Prepared Label', left: M + 20, top: 854, width: 296, text: 'PREPARED BY', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          lines({ name: 'Prepared Detail', left: M + 20, top: 876, width: 296, text: 'Priya Patel — Analytics Lead\npriya@atlascreative.co', size: 10.5, weight: 500, color: BODY_COLOR, lineHeight: 1.6 }),
          rect({ name: 'Period Card', left: 416, top: 836, w: 336, h: 104, fill: CARD, radius: 8 }),
          text({ name: 'Period Label', left: 436, top: 854, width: 296, text: 'REPORTING PERIOD', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          lines({ name: 'Period Detail', left: 436, top: 876, width: 296, text: 'June 1 – June 30, 2026\nDelivered July 8, 2026', size: 10.5, weight: 500, color: BODY_COLOR, lineHeight: 1.6 }),
          rect({ name: 'Cover Footer Rule', left: M, top: 976, w: CW, h: 1, fill: RULE }),
          text({ name: 'Cover Footer Left', left: M, top: 990, width: 420, text: 'Summit Physical Therapy · Monthly Report · June 2026', size: 9, weight: 500, color: FAINT }),
          text({ name: 'Cover Footer Right', left: PAGE_W - M - 160, top: 990, width: 160, text: 'Page 1 of 5', size: 9, weight: 500, color: FAINT, align: 'right' }),
        ],
      },
      {
        label: 'Traffic & Acquisition',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TEAL, docLabel: 'Monthly Report — Summit Physical Therapy', footerLeft: 'Atlas Creative Studio · June 2026 Report', page: 2, total: 5 }),
          ...pageTitle({ kicker: '01 · Traffic & Acquisition', title: 'Sessions hit a twelve-month high', sub: 'Total sessions grew 18.2% month over month, and every paid dollar now maps to a tracked outcome.', accent: TEAL }).layers,
          graph({
            name: 'Sessions by Month',
            left: M,
            top: 204,
            w: CW,
            h: 290,
            kind: 'bar',
            bindings: [
              { label: 'Jan', fallbackValue: 9.9, fill: solid('#94a3b8') },
              { label: 'Feb', fallbackValue: 10.4, fill: solid('#94a3b8') },
              { label: 'Mar', fallbackValue: 11.3, fill: solid('#94a3b8') },
              { label: 'Apr', fallbackValue: 12.2, fill: solid('#64748b') },
              { label: 'May', fallbackValue: 13.1, fill: solid('#64748b') },
              { label: 'Jun', fallbackValue: 14.2, fill: solid(TEAL) },
            ],
            options: {
              orientation: 'vertical',
              growDirection: 'positive',
              minValue: 0,
              maxValue: 16,
              barSpacing: 0.4,
              cornerRadius: 4,
              seriesLabels: { mode: 'text', position: 'bottom', fontSize: 10, color: '#64748b', gap: 8 },
              valueLabels: { mode: 'above', fontSize: 9.5, color: '#334155', decimals: 1 },
            },
          }),
          text({ name: 'Chart Caption', left: M, top: 524, width: CW, text: 'Sessions by month, in thousands — teal marks the reporting month.', size: 9, weight: 500, color: FAINT, italic: true }),
          ...sectionLabel({ label: 'What moved', top: 548, accent: TEAL }),
          para({ name: 'Traffic Insight 1', left: M, top: 584, width: CW, height: 38, text: '•   Organic sessions rose 11% on the June service-page refresh; three revenue pages entered the top five positions.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          para({ name: 'Traffic Insight 2', left: M, top: 628, width: CW, height: 38, text: '•   Paid search CTR improved from 3.1% to 4.4% after the ad-group restructure and a fresh negative-keyword pass.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          para({ name: 'Traffic Insight 3', left: M, top: 672, width: CW, height: 38, text: '•   Referral traffic doubled off the June PT-association feature — two similar placements are already pitched for July.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          ...kpiCard({ name: 'Users New', left: 64, top: 732, w: 336, h: 92, label: 'New users', value: '9,480', delta: '+21.4% vs May', accent: TEAL }),
          ...kpiCard({ name: 'Users Returning', left: 416, top: 732, w: 336, h: 92, label: 'Returning users', value: '4,730', delta: '+12.7% vs May', accent: TEAL }),
        ],
      },
      {
        label: 'Channel Mix',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TEAL, docLabel: 'Monthly Report — Summit Physical Therapy', footerLeft: 'Atlas Creative Studio · June 2026 Report', page: 3, total: 5 }),
          ...pageTitle({ kicker: '02 · Channel Mix', title: 'Where June sessions came from', accent: TEAL }).layers,
          graph({
            name: 'Channel Donut',
            left: M,
            top: 200,
            w: 300,
            h: 300,
            kind: 'donut',
            bindings: [
              { label: 'Organic', fallbackValue: 5840, fill: solid(TEAL) },
              { label: 'Paid search', fallbackValue: 3610, fill: solid('#14b8a6') },
              { label: 'Direct', fallbackValue: 2420, fill: solid('#475569') },
              { label: 'Social', fallbackValue: 1480, fill: solid('#94a3b8') },
              { label: 'Referral', fallbackValue: 860, fill: solid('#cbd5e1') },
            ],
            options: { innerRadiusRatio: 0.66, sortMode: 'none', sliceLabels: { mode: 'none' }, legend: { enabled: false } },
          }),
          text({ name: 'Donut Center Value', left: 104, top: 328, width: 220, text: '14,210', size: 26, weight: 800, color: INK, align: 'center' }),
          text({ name: 'Donut Center Caption', left: 104, top: 362, width: 220, text: 'total sessions', size: 10, weight: 500, color: MUTED, align: 'center' }),
          ...[
            { name: 'Organic', color: TEAL, value: '5,840 · 41%' },
            { name: 'Paid search', color: '#14b8a6', value: '3,610 · 25%' },
            { name: 'Direct', color: '#475569', value: '2,420 · 17%' },
            { name: 'Social', color: '#94a3b8', value: '1,480 · 11%' },
            { name: 'Referral', color: '#cbd5e1', value: '860 · 6%' },
          ].flatMap((legendRow, i) => {
            const y = 216 + i * 42;
            return [
              rect({ name: `Legend ${legendRow.name} Dot`, left: 420, top: y + 2, w: 10, h: 10, fill: legendRow.color, radius: 2 }),
              text({ name: `Legend ${legendRow.name}`, left: 442, top: y, width: 160, text: legendRow.name, size: 11, weight: 600, color: INK }),
              text({ name: `Legend ${legendRow.name} Value`, left: 610, top: y + 1, width: 142, text: legendRow.value, size: 10.5, weight: 500, color: MUTED, align: 'right' }),
              ...(i < 4 ? [rect({ name: `Legend ${legendRow.name} Rule`, left: 420, top: y + 28, w: 332, h: 1, fill: RULE })] : []),
            ];
          }),
          ...table({
            name: 'Channels',
            top: 548,
            cols: [
              { label: 'Channel', left: 80, width: 200 },
              { label: 'Sessions', left: 340, width: 100, align: 'right' },
              { label: 'Leads', left: 470, width: 80, align: 'right' },
              { label: 'Conv. rate', left: 616, width: 120, align: 'right' },
            ],
            rows: [
              ['Organic search', '5,840', '178', '3.05%'],
              ['Paid search', '3,610', '132', '3.66%'],
              ['Direct', '2,420', '38', '1.57%'],
              ['Social', '1,480', '22', '1.49%'],
              ['Referral', '860', '16', '1.86%'],
            ],
            headFill: SLATE,
            strongCol: 0,
          }).layers,
          rect({ name: 'Channel Insight Card', left: M, top: 772, w: CW, h: 88, fill: '#e6f2f0', radius: 8 }),
          rect({ name: 'Channel Insight Tick', left: M, top: 772, w: 3, h: 88, fill: TEAL }),
          text({ name: 'Channel Insight Label', left: M + 24, top: 788, width: 300, text: 'TAKEAWAY', size: 8.5, weight: 700, color: TEAL, tracking: 1.4 }),
          para({ name: 'Channel Insight Body', left: M + 24, top: 808, width: 640, height: 40, text: 'Paid search converts at double the site average. July shifts another $1,200 per month into the four best-performing ad groups.', size: 11, weight: 500, color: '#134e4a', lineHeight: 1.55 }),
        ],
      },
      {
        label: 'Organic Search',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TEAL, docLabel: 'Monthly Report — Summit Physical Therapy', footerLeft: 'Atlas Creative Studio · June 2026 Report', page: 4, total: 5 }),
          ...pageTitle({ kicker: '03 · Organic Search', title: 'Clicks up 34% since January', accent: TEAL }).layers,
          ...kpiCard({ name: 'GSC Clicks', left: 64, top: 188, w: 218, h: 92, label: 'Clicks (June)', value: '6,910', delta: '+9.8% vs May', accent: TEAL }),
          ...kpiCard({ name: 'GSC Impressions', left: 299, top: 188, w: 218, h: 92, label: 'Impressions', value: '214,300', delta: '+6.2% vs May', accent: TEAL, valueSize: 21 }),
          ...kpiCard({ name: 'GSC Position', left: 534, top: 188, w: 218, h: 92, label: 'Avg. position', value: '4.6', delta: 'Up from 5.3 in May', accent: TEAL }),
          graph({
            name: 'Organic Clicks Trend',
            left: M,
            top: 320,
            w: CW,
            h: 250,
            kind: 'line',
            bindings: [
              {
                label: 'Organic clicks',
                fill: solid(TEAL),
                segments: [
                  { label: 'Jan', fallbackValue: 5150 },
                  { label: 'Feb', fallbackValue: 5480 },
                  { label: 'Mar', fallbackValue: 5920 },
                  { label: 'Apr', fallbackValue: 6180 },
                  { label: 'May', fallbackValue: 6290 },
                  { label: 'Jun', fallbackValue: 6910 },
                ],
              },
            ],
            options: { curve: 'smooth', lineWidth: 3, showDataPoints: true, dataPointSize: 6, minValue: 4000, maxValue: 7500, legend: { enabled: false } },
          }),
          text({ name: 'Trend Caption', left: M, top: 584, width: CW, text: 'Organic clicks by month, January through June 2026.', size: 9, weight: 500, color: FAINT, italic: true }),
          ...table({
            name: 'Top Queries',
            top: 620,
            cols: [
              { label: 'Query', left: 80, width: 300 },
              { label: 'Clicks', left: 400, width: 80, align: 'right' },
              { label: 'Avg. position', left: 500, width: 96, align: 'right' },
              { label: 'Vs. May', left: 616, width: 120, align: 'right' },
            ],
            rows: [
              ['physical therapy near me', '1,240', '3.2', '+18%'],
              ['sports injury rehab', '620', '4.1', '+11%'],
              ['back pain treatment', '540', '5.8', '+6%'],
              ['post-surgery rehab protocol', '410', '2.9', '+24%'],
              ['dry needling therapy', '380', '6.4', 'new'],
            ],
            headFill: SLATE,
            strongCol: 0,
          }).layers,
          text({ name: 'Queries Caption', left: M, top: 836, width: CW, text: 'Positions are June averages from Search Console; “new” marks a first-time top-ten entry.', size: 9, weight: 500, color: FAINT, italic: true }),
        ],
      },
      {
        label: 'Recommendations',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TEAL, docLabel: 'Monthly Report — Summit Physical Therapy', footerLeft: 'Atlas Creative Studio · June 2026 Report', page: 5, total: 5 }),
          ...pageTitle({ kicker: '04 · Recommendations', title: 'Three moves for July', sub: 'Prioritized by expected impact against effort — owners already assigned.', accent: TEAL }).layers,
          ...table({
            name: 'Priorities',
            top: 200,
            cols: [
              { label: '#', left: 80, width: 30 },
              { label: 'Recommendation', left: 130, width: 330 },
              { label: 'Impact', left: 476, width: 70 },
              { label: 'Effort', left: 562, width: 70 },
              { label: 'Owner', left: 656, width: 80, align: 'right' },
            ],
            rows: [
              ['1', 'Shift $1,200/mo into the top four paid ad groups', 'High', 'Low', 'Atlas'],
              ['2', 'Publish condition-page clusters: knee & shoulder', 'High', 'Medium', 'Atlas'],
              ['3', 'Automate review requests after discharge visits', 'Medium', 'Low', 'Summit'],
            ],
            headFill: SLATE,
            rowH: 40,
            strongCol: 1,
          }).layers,
          ...sectionLabel({ label: 'July targets', top: 388, accent: TEAL }),
          ...kpiCard({ name: 'Target Sessions', left: 64, top: 424, w: 218, h: 96, label: 'Sessions', value: '15,500', delta: 'vs 14,210 in June', deltaColor: MUTED, accent: TEAL }),
          ...kpiCard({ name: 'Target Leads', left: 299, top: 424, w: 218, h: 96, label: 'Qualified leads', value: '425', delta: 'vs 386 in June', deltaColor: MUTED, accent: TEAL }),
          ...kpiCard({ name: 'Target CPL', left: 534, top: 424, w: 218, h: 96, label: 'Cost per lead', value: '≤ $36.00', delta: 'vs $38.20 in June', deltaColor: MUTED, accent: TEAL, valueSize: 21 }),
          ...sectionLabel({ label: 'Also on our radar', top: 560, accent: TEAL }),
          para({ name: 'Radar 1', left: M, top: 596, width: CW, height: 38, text: '•   Google Business Profile: June review velocity slowed to 4/month — the discharge-visit automation above should restore 10–12.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          para({ name: 'Radar 2', left: M, top: 640, width: CW, height: 38, text: '•   Site speed: the booking widget adds 800ms on mobile. Vendor fix ships mid-July; we will verify with real-user data.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
          rect({ name: 'Sign-off Card', left: M, top: 716, w: 336, h: 118, fill: CARD, radius: 8 }),
          text({ name: 'Sign-off Label', left: M + 20, top: 734, width: 296, text: 'PREPARED BY', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          lines({ name: 'Sign-off Detail', left: M + 20, top: 756, width: 296, text: 'Priya Patel — Analytics Lead\npriya@atlascreative.co\n(415) 555-0134', size: 10.5, weight: 500, color: BODY_COLOR, lineHeight: 1.6 }),
          rect({ name: 'Next Review Card', left: 416, top: 716, w: 336, h: 118, fill: '#e6f2f0', radius: 8 }),
          text({ name: 'Next Review Label', left: 436, top: 734, width: 296, text: 'NEXT REVIEW', size: 8.5, weight: 700, color: TEAL, tracking: 1.4 }),
          lines({ name: 'Next Review Detail', left: 436, top: 756, width: 296, text: 'Thursday, August 6, 2026\n10:00 AM PT · video call\nInvite already on your calendar', size: 10.5, weight: 500, color: '#134e4a', lineHeight: 1.6 }),
        ],
      },
    ],
  },

  /* ════════════════ 5. NDA — burgundy, FlowText body ════════════════ */
  {
    id: 'nda-pack',
    title: 'Mutual Non-Disclosure Agreement',
    shortLabel: 'Mutual NDA',
    description: 'Three-page mutual NDA with complete clause structure — definitions, exclusions, obligations, compelled disclosure, term, remedies — and an execution page.',
    icon: 'fas fa-shield-halved',
    composerPresetId: 'nda-standard',
    buildDocumentLayers: (nonce: string) => {
      const id = 'nda-pack';
      const b = (suffix: string, type: string, t: string) => fb(id, nonce, suffix, type, t);
      return [flowLegalBody(id, nonce, [
        b('h1', 'heading1', 'Mutual Non-Disclosure Agreement'),
        b('intro', 'paragraph', 'This Mutual Non-Disclosure Agreement (the “Agreement”) is made as of October 12, 2026 (the “Effective Date”) between Atlas Creative Studio LLC, of 548 Market Street, Suite 210, San Francisco, CA 94104, and Northwind Labs, Inc., of 2200 Elliott Avenue, Seattle, WA 98121 (each a “Party,” together the “Parties”).'),
        b('purpose', 'paragraph', 'The Parties wish to explore a potential brand and product-launch engagement (the “Purpose”) and expect to exchange information that is confidential. In consideration of that exchange, the Parties agree as follows.'),
        b('s1t', 'heading2', '1.  CONFIDENTIAL INFORMATION'),
        b('s1a', 'paragraph', '“Confidential Information” means any non-public information disclosed by one Party to the other, in any form, that is designated confidential or that a reasonable person would understand to be confidential, including:'),
        b('s1b', 'bulletList', 'Business plans, budgets, pricing, forecasts, and unreleased product or launch plans'),
        b('s1c', 'bulletList', 'Technical information: designs, source code, prototypes, research, and know-how'),
        b('s1d', 'bulletList', 'Customer, audience, and analytics data, including performance metrics'),
        b('s1e', 'bulletList', 'The existence and terms of the Parties’ discussions regarding the Purpose'),
        b('s2t', 'heading2', '2.  EXCLUSIONS'),
        b('s2a', 'paragraph', 'Confidential Information does not include information that the receiving Party can demonstrate:'),
        b('s2b', 'bulletList', 'Is or becomes publicly available through no breach of this Agreement'),
        b('s2c', 'bulletList', 'Was rightfully known to it, without restriction, before disclosure'),
        b('s2d', 'bulletList', 'Is independently developed without use of the disclosing Party’s information'),
        b('s2e', 'bulletList', 'Is rightfully received from a third party without a duty of confidentiality'),
        b('s3t', 'heading2', '3.  OBLIGATIONS'),
        b('s3a', 'paragraph', 'The receiving Party will:'),
        b('s3b', 'numberedList', 'Use Confidential Information solely to evaluate and pursue the Purpose'),
        b('s3c', 'numberedList', 'Protect it with at least the care used for its own confidential information, and never less than reasonable care'),
        b('s3d', 'numberedList', 'Limit access to employees, contractors, and advisors who need it for the Purpose and are bound by obligations at least as protective'),
        b('s3e', 'numberedList', 'Notify the disclosing Party promptly upon learning of any unauthorized use or disclosure'),
        b('s4t', 'heading2', '4.  COMPELLED DISCLOSURE'),
        b('s4a', 'paragraph', 'If the receiving Party is required by law, regulation, or court order to disclose Confidential Information, it will (where lawful) give the disclosing Party prompt written notice and reasonable cooperation to seek protective treatment, and will disclose only the portion legally required.'),
        b('s5t', 'heading2', '5.  TERM'),
        b('s5a', 'paragraph', 'This Agreement applies to disclosures made within two (2) years of the Effective Date. Obligations for each item of Confidential Information continue for three (3) years from its disclosure; obligations for trade secrets continue for as long as the information remains a trade secret under applicable law. Expiration does not excuse any breach that occurred before it.'),
        b('s6t', 'heading2', '6.  RETURN OR DESTRUCTION'),
        b('s6a', 'paragraph', 'On written request or when discussions end, each Party will promptly return or destroy the other’s Confidential Information and certify destruction on request, except one archival copy may be retained where required by law or bona fide document-retention policy, remaining subject to this Agreement.'),
        b('s7t', 'heading2', '7.  NO LICENSE; RESERVATION OF RIGHTS'),
        b('s7a', 'paragraph', 'All Confidential Information remains the property of the disclosing Party. No license or other right, express or implied, is granted under any patent, copyright, trademark, or trade secret by disclosure under this Agreement.'),
        b('s8t', 'heading2', '8.  NO WARRANTY'),
        b('s8a', 'paragraph', 'CONFIDENTIAL INFORMATION IS PROVIDED “AS IS.” NEITHER PARTY MAKES ANY WARRANTY, EXPRESS OR IMPLIED, AS TO ITS ACCURACY OR COMPLETENESS, AND NEITHER PARTY IS LIABLE FOR THE OTHER’S USE OF OR RELIANCE ON IT.'),
        b('s9t', 'heading2', '9.  NO OBLIGATION TO PROCEED'),
        b('s9a', 'paragraph', 'Nothing in this Agreement obligates either Party to proceed with any transaction, and each Party may end discussions at any time. This Agreement does not create any agency, partnership, or joint venture.'),
        b('s10t', 'heading2', '10.  REMEDIES'),
        b('s10a', 'paragraph', 'The Parties agree that unauthorized use or disclosure may cause harm that money cannot adequately remedy. The disclosing Party is entitled to seek injunctive or other equitable relief without posting a bond, in addition to all other remedies available at law or in equity, and the prevailing Party in any action to enforce this Agreement may recover its reasonable attorneys’ fees.'),
        b('s10bt', 'heading2', '11.  NO ASSIGNMENT'),
        b('s10ba', 'paragraph', 'Neither Party may assign this Agreement without the other Party’s prior written consent, except to a successor in connection with a merger, reorganization, or sale of substantially all assets, in which case the assignee remains bound by this Agreement. Any attempted assignment in violation of this section is void.'),
        b('s10ct', 'heading2', '12.  NOTICES'),
        b('s10ca', 'paragraph', 'Notices under this Agreement must be in writing and are deemed given when delivered by hand or courier, or when sent by email to the addresses above with confirmation of transmission, addressed to the signatory identified below or a successor identified in writing.'),
        b('s11t', 'heading2', '13.  GOVERNING LAW AND VENUE'),
        b('s11a', 'paragraph', 'This Agreement is governed by the laws of the State of California, without regard to conflict-of-laws rules. The state and federal courts located in San Francisco County have exclusive jurisdiction over any dispute arising out of this Agreement, and each Party consents to personal jurisdiction and venue there and waives any objection of inconvenient forum.'),
        b('s12t', 'heading2', '14.  ENTIRE AGREEMENT; COUNTERPARTS'),
        b('s12a', 'paragraph', 'This Agreement is the Parties’ entire agreement on confidentiality for the Purpose, supersedes prior discussions and understandings on that subject, and may be amended only in a writing signed by both Parties. It may be executed in counterparts, including by reliable electronic signature, each of which is deemed an original and all of which together form one instrument.'),
        b('sig-div', 'divider', ''),
        b('sig-t', 'heading2', 'EXECUTION'),
        b('sig-lead', 'paragraph', 'Agreed and accepted by the Parties’ authorized representatives as of the Effective Date.'),
        b('sig-a-t', 'heading3', 'ATLAS CREATIVE STUDIO LLC'),
        b('sig-a-1', 'paragraph', 'Signature:  ______________________________________'),
        b('sig-a-2', 'paragraph', 'Name / Title:  Jordan Reyes, Principal'),
        b('sig-a-3', 'paragraph', 'Date:  ______________________'),
        b('sig-b-t', 'heading3', 'NORTHWIND LABS, INC.'),
        b('sig-b-1', 'paragraph', 'Signature:  ______________________________________'),
        b('sig-b-2', 'paragraph', 'Name / Title:  ______________________________________'),
        b('sig-b-3', 'paragraph', 'Date:  ______________________'),
      ], { pages: 3, accent: BURGUNDY })];
    },
    pages: [1, 2, 3].map((n) => ({
      label: n === 1 ? 'Agreement' : n === 3 ? 'Execution' : 'Terms',
      ...LETTER_PAGE,
      layers: [
        ...pageChrome({ accent: BURGUNDY, docLabel: 'Mutual Non-Disclosure Agreement', footerLeft: 'Atlas Creative Studio · Mutual NDA — Northwind Labs', page: n, total: 3 }),
        ...(n === 1
          ? [text({ name: 'Confidential Tag', left: PAGE_W - M - 300, top: 46, width: 300, text: 'Confidential — for the named parties only', size: 8.5, weight: 500, color: FAINT, align: 'right' })]
          : []),
      ],
    })),
  },

  /* ════════════════ 6. STATEMENT OF WORK — steel blue ════════════════ */
  {
    id: 'statement-of-work-pack',
    title: 'Statement of Work',
    shortLabel: 'Statement of Work',
    description: 'Four-page SOW: objectives and success criteria, deliverables with acceptance criteria and dates, responsibility split, and milestone-tied fees with signatures.',
    icon: 'fas fa-list-check',
    composerPresetId: 'scope-of-work',
    pages: [
      {
        label: 'Overview & Objectives',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: STEEL, docLabel: 'Statement of Work — SOW 2026-014', footerLeft: 'Atlas Creative Studio · SOW 2026-014 — Cascade Dental Group', page: 1, total: 4 }),
          ...pageTitle({ kicker: 'Statement of Work', title: 'Patient-acquisition website rebuild', sub: 'Cascade Dental Group · Executed under the Master Services Agreement dated March 2, 2026.', accent: STEEL }).layers,
          ...kpiCard({ name: 'SOW Number', left: 64, top: 200, w: 218, h: 92, label: 'SOW number', value: '2026-014', delta: 'Version 1.0', deltaColor: MUTED, accent: STEEL, valueSize: 20 }),
          ...kpiCard({ name: 'SOW Start', left: 299, top: 200, w: 218, h: 92, label: 'Kickoff', value: 'August 3', delta: 'Launch target — Oct 19', deltaColor: MUTED, accent: STEEL, valueSize: 20 }),
          ...kpiCard({ name: 'SOW Fee', left: 534, top: 200, w: 218, h: 92, label: 'Fixed fee', value: '$28,400', delta: 'Milestone-billed 40 / 40 / 20', deltaColor: MUTED, accent: STEEL, valueSize: 20 }),
          ...sectionLabel({ label: 'Project summary', top: 332, accent: STEEL }),
          para({ name: 'Project Summary', left: M, top: 368, width: CW, height: 80, text: 'Atlas will design, build, and launch a new marketing website for Cascade Dental Group’s three clinics, focused on new-patient bookings: fifteen page templates, an insurance and pricing explainer, provider profiles, and online booking integrated with NexHealth. The current WordPress site is retired at cutover, with all 140 pages redirected.', size: 12, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          ...sectionLabel({ label: 'Objectives', top: 472, accent: STEEL }),
          ...[
            'Lift new-patient conversions (forms + tracked calls) from 1.8% to 3.0% of sessions',
            'Cut mobile page load (LCP) from 5.4s to under 2.5s on every template',
            'Let clinic staff edit any page without developer help',
          ].flatMap((objective, i) => [
            text({ name: `Objective ${i + 1} Number`, left: M, top: 508 + i * 32, width: 26, text: `${i + 1}.`, size: 12, weight: 700, color: STEEL }),
            text({ name: `Objective ${i + 1}`, left: 96, top: 508 + i * 32, width: 656, text: objective, size: 11.5, weight: 500, color: BODY_COLOR }),
          ]),
          ...sectionLabel({ label: 'Success criteria', top: 622, accent: STEEL }),
          ...table({
            name: 'Success Criteria',
            top: 658,
            cols: [
              { label: 'Metric', left: 80, width: 250 },
              { label: 'Baseline', left: 350, width: 90, align: 'right' },
              { label: 'Target', left: 460, width: 90, align: 'right' },
              { label: 'Measured by', left: 596, width: 140, align: 'right' },
            ],
            rows: [
              ['New-patient conversions / month', '96', '160', 'GA4 + call tracking'],
              ['Mobile LCP (75th percentile)', '5.4s', '≤ 2.5s', 'CrUX field data'],
              ['Booking completion rate', '44%', '60%', 'NexHealth funnel'],
            ],
            headFill: STEEL,
            strongCol: 0,
          }).layers,
          rect({ name: 'MSA Note Card', left: M, top: 828, w: CW, h: 92, fill: CARD, radius: 8 }),
          text({ name: 'MSA Note Label', left: M + 24, top: 846, width: 400, text: 'RELATIONSHIP TO MSA', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          para({ name: 'MSA Note Body', left: M + 24, top: 866, width: 640, height: 40, text: 'This SOW is governed by the Master Services Agreement dated March 2, 2026. If this SOW conflicts with the MSA, the MSA controls unless this SOW expressly amends it by section reference.', size: 10.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
        ],
      },
      {
        label: 'Deliverables & Schedule',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: STEEL, docLabel: 'Statement of Work — SOW 2026-014', footerLeft: 'Atlas Creative Studio · SOW 2026-014 — Cascade Dental Group', page: 2, total: 4 }),
          ...pageTitle({ kicker: '02 · Deliverables', title: 'What we hand over, and when', sub: 'Each deliverable carries its own acceptance criteria — no vague “done”.', accent: STEEL }).layers,
          ...[
            { code: 'D1', name: 'Discovery readout & measurement plan', criteria: 'Accepted when leadership signs off on priorities, KPI definitions, and the tracking plan.', due: 'Aug 14' },
            { code: 'D2', name: 'Design system & 15 template designs', criteria: 'Accepted when the Figma library passes the review checklist after one consolidated revision round.', due: 'Sep 11' },
            { code: 'D3', name: 'Developed site on staging', criteria: 'Accepted when every template passes QA: WCAG 2.2 AA, LCP ≤ 2.5s, booking flow tested end-to-end.', due: 'Oct 9' },
            { code: 'D4', name: 'Content migration & redirect map', criteria: 'Accepted when all 140 pages are migrated with zero broken internal links and verified redirects.', due: 'Oct 16' },
            { code: 'D5', name: 'Launch + 30-day stabilization', criteria: 'Accepted 30 days after go-live with the error budget met and two staff training sessions delivered.', due: 'Nov 20' },
          ].flatMap((d, i) => {
            const y = 200 + i * 96;
            return [
              rect({ name: `${d.code} Chip`, left: M, top: y, w: 40, h: 22, fill: STEEL, radius: 11 }),
              text({ name: `${d.code} Chip Text`, left: M, top: y + 6, width: 40, text: d.code, size: 9, weight: 700, color: '#ffffff', align: 'center' }),
              text({ name: `${d.code} Name`, left: 130, top: y, width: 440, text: d.name, size: 13.5, weight: 700, color: INK }),
              para({ name: `${d.code} Criteria`, left: 130, top: y + 24, width: 440, height: 36, text: d.criteria, size: 10.5, weight: 400, color: MUTED, lineHeight: 1.5 }),
              text({ name: `${d.code} Due Label`, left: 620, top: y, width: 132, text: 'DUE', size: 8.5, weight: 700, color: FAINT, align: 'right', tracking: 1.4 }),
              text({ name: `${d.code} Due`, left: 620, top: y + 18, width: 132, text: d.due, size: 12, weight: 700, color: STEEL, align: 'right' }),
              ...(i < 4 ? [rect({ name: `${d.code} Rule`, left: M, top: y + 76, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          ...sectionLabel({ label: 'Schedule at a glance', top: 704, accent: STEEL }),
          ...table({
            name: 'Schedule',
            top: 740,
            cols: [
              { label: 'Phase', left: 80, width: 160 },
              { label: 'Window', left: 300, width: 200 },
              { label: 'Exit gate', left: 556, width: 180, align: 'right' },
            ],
            rows: [
              ['Discovery', 'Aug 3 – Aug 14', 'Strategy sign-off'],
              ['Design', 'Aug 17 – Sep 11', 'Design approval'],
              ['Build', 'Sep 14 – Oct 9', 'Staging acceptance'],
              ['Launch & stabilize', 'Oct 19 – Nov 20', 'Stabilization exit'],
            ],
            headFill: STEEL,
            rowH: 32,
            strongCol: 0,
          }).layers,
        ],
      },
      {
        label: 'Responsibilities',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: STEEL, docLabel: 'Statement of Work — SOW 2026-014', footerLeft: 'Atlas Creative Studio · SOW 2026-014 — Cascade Dental Group', page: 3, total: 4 }),
          ...pageTitle({ kicker: '03 · Responsibilities', title: 'Who owns what', sub: 'A project this fast only works when both sides keep their lane clear.', accent: STEEL }).layers,
          rect({ name: 'Atlas Card', left: 64, top: 200, w: 336, h: 226, fill: CARD, radius: 8 }),
          rect({ name: 'Atlas Card Tick', left: 64, top: 200, w: 3, h: 226, fill: STEEL }),
          text({ name: 'Atlas Card Label', left: 88, top: 218, width: 290, text: 'ATLAS PROVIDES', size: 9, weight: 700, color: STEEL, tracking: 1.4 }),
          lines({ name: 'Atlas Card List', left: 88, top: 244, width: 290, text: '•  Project management & weekly demos\n•  All UX, design, and development labor\n•  QA, accessibility & performance testing\n•  Analytics and call-tracking configuration\n•  CMS training — two live sessions, recorded\n•  Launch runbook and rollback plan', size: 10, weight: 500, color: BODY_COLOR, lineHeight: 1.75 }),
          rect({ name: 'Client Card', left: 416, top: 200, w: 336, h: 226, fill: CARD, radius: 8 }),
          rect({ name: 'Client Card Tick', left: 416, top: 200, w: 3, h: 226, fill: STEEL }),
          text({ name: 'Client Card Label', left: 440, top: 218, width: 290, text: 'CASCADE PROVIDES', size: 9, weight: 700, color: STEEL, tracking: 1.4 }),
          lines({ name: 'Client Card List', left: 440, top: 244, width: 290, text: '•  One decision-maker for approvals\n•  Brand assets, photography & clinic data\n•  Insurance and pricing content sign-off\n•  NexHealth admin access by August 21\n•  Staff availability for week-14 training\n•  Consolidated feedback within 5 business days', size: 10, weight: 500, color: BODY_COLOR, lineHeight: 1.75 }),
          ...sectionLabel({ label: 'Assumptions', top: 466, accent: STEEL }),
          ...[
            'Copywriting for up to 20 pages is included; additional pages are change-ordered.',
            'Photography is client-supplied; stock imagery is licensed at cost with prior approval.',
            'Hosting stays on Cascade’s WP Engine account; Atlas configures staging there.',
          ].flatMap((assumption, i) => [
            rect({ name: `Assumption ${i + 1} Bullet`, left: M, top: 507 + i * 28, w: 5, h: 5, fill: STEEL }),
            text({ name: `Assumption ${i + 1}`, left: 80, top: 502 + i * 28, width: 660, text: assumption, size: 11, weight: 500, color: BODY_COLOR }),
          ]),
          rect({ name: 'Out of Scope Card', left: M, top: 612, w: CW, h: 118, fill: '#e9f0f7', radius: 8 }),
          rect({ name: 'Out of Scope Tick', left: M, top: 612, w: 3, h: 118, fill: STEEL }),
          text({ name: 'Out of Scope Label', left: M + 24, top: 630, width: 400, text: 'EXPLICITLY OUT OF SCOPE', size: 8.5, weight: 700, color: STEEL, tracking: 1.4 }),
          lines({ name: 'Out of Scope List', left: M + 24, top: 652, width: 640, text: '•  Ongoing SEO or paid-media retainers (quoted separately)\n•  Patient-portal or EHR integrations beyond NexHealth booking\n•  Languages beyond the English and Spanish templates', size: 10.5, weight: 500, color: BODY_COLOR, lineHeight: 1.75 }),
          ...sectionLabel({ label: 'Change control', top: 770, accent: STEEL }),
          para({ name: 'Change Control Body', left: M, top: 806, width: CW, height: 40, text: 'Changes to scope, schedule, or fees require a written change order signed by both parties before work proceeds. Estimates under $2,500 come back the same business day.', size: 11, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
        ],
      },
      {
        label: 'Fees & Acceptance',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: STEEL, docLabel: 'Statement of Work — SOW 2026-014', footerLeft: 'Atlas Creative Studio · SOW 2026-014 — Cascade Dental Group', page: 4, total: 4 }),
          ...pageTitle({ kicker: '04 · Fees & Acceptance', title: 'Milestone-tied, fixed fee', accent: STEEL }).layers,
          ...table({
            name: 'Fees',
            top: 180,
            cols: [
              { label: 'Payment', left: 80, width: 180 },
              { label: 'Tied to', left: 300, width: 240 },
              { label: 'Amount', left: 616, width: 120, align: 'right' },
            ],
            rows: [
              ['Deposit — 40%', 'SOW execution', '$11,360'],
              ['Progress — 40%', 'Design approval (D2)', '$11,360'],
              ['Final — 20%', 'Launch week (D5 start)', '$5,680'],
            ],
            headFill: STEEL,
            strongCol: 2,
          }).layers,
          rect({ name: 'Fee Total Band', left: M, top: 312, w: CW, h: 40, fill: STEEL, radius: 4 }),
          text({ name: 'Fee Total Label', left: 80, top: 326, width: 300, text: 'TOTAL FIXED FEE', size: 10, weight: 700, color: '#dbe6f2', tracking: 1.6 }),
          text({ name: 'Fee Total Value', left: 616, top: 323, width: 120, text: '$28,400', size: 15, weight: 700, color: '#ffffff', align: 'right' }),
          ...[
            'Invoices are due net 15; late balances follow MSA §5.',
            'The fee is fixed for the scope in this SOW — no hourly surprises.',
            'Approved expenses (stock, plugins) bill at cost, capped at $900 without fresh approval.',
          ].flatMap((term, i) => [
            rect({ name: `Fee Term ${i + 1} Bullet`, left: M, top: 393 + i * 28, w: 5, h: 5, fill: STEEL }),
            text({ name: `Fee Term ${i + 1}`, left: 80, top: 388 + i * 28, width: 660, text: term, size: 11, weight: 500, color: BODY_COLOR }),
          ]),
          ...sectionLabel({ label: 'Acceptance of deliverables', top: 496, accent: STEEL }),
          para({ name: 'Acceptance Body', left: M, top: 532, width: CW, height: 58, text: 'Each deliverable is accepted when its criteria on page 2 are met, or automatically five business days after delivery if no written rejection describes the specific gap. Rejections get one cure round before the change-order process applies.', size: 11, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          ...sectionLabel({ label: 'Authorization', top: 622, accent: STEEL }),
          text({ name: 'Authorization Intro', left: M, top: 658, width: CW, text: 'Signed by authorized representatives of each party, effective as of the last date below.', size: 11.5, weight: 400, color: BODY_COLOR }),
          ...signatureBlock({ name: 'Cascade Signature', left: M, top: 702, w: 328, party: 'Cascade Dental Group', accent: STEEL }),
          ...signatureBlock({ name: 'Atlas Signature', left: 424, top: 702, w: 328, party: 'Atlas Creative Studio', accent: STEEL }),
        ],
      },
    ],
  },

  /* ════════════════ 7. CLIENT ONBOARDING — deep teal ════════════════ */
  {
    id: 'client-onboarding-pack',
    title: 'Client Welcome Packet',
    shortLabel: 'Client Onboarding',
    description: 'Four-page welcome packet: first-14-days plan, team and communication guide, 90-day roadmap with client checklist, and tools, access, and FAQ.',
    icon: 'fas fa-handshake',
    composerPresetId: 'doc-client-onboarding',
    pages: [
      {
        label: 'Welcome',
        ...LETTER_PAGE,
        layers: [
          rect({ name: 'Welcome Panel', left: 0, top: 0, w: PAGE_W, h: 300, fill: DEEP_TEAL }),
          text({ name: 'Studio Wordmark', left: M, top: 52, width: 400, text: 'ATLAS CREATIVE STUDIO', size: 10, weight: 700, color: '#9ed4cb', tracking: 2.2 }),
          text({ name: 'Welcome Date', left: PAGE_W - M - 200, top: 52, width: 200, text: 'JULY 2026', size: 10, weight: 600, color: '#7fbcb2', align: 'right', tracking: 2 }),
          text({ name: 'Welcome Eyebrow', left: M, top: 118, width: 480, text: 'CLIENT ONBOARDING', size: 10.5, weight: 700, color: '#8fd0c6', tracking: 2.6 }),
          text({ name: 'Welcome Title', left: M, top: 142, width: 660, text: 'Welcome, Juniper Home Goods', size: 32, weight: 700, color: '#ffffff' }),
          para({ name: 'Welcome Subtitle', left: M, top: 196, width: 620, height: 42, text: 'Everything you need for a smooth first month with Atlas — who to ping, what happens when, and the few things we need from you.', size: 12.5, weight: 400, color: '#cde8e3', lineHeight: 1.55 }),
          ...sectionLabel({ label: 'Your first 14 days', top: 340, accent: DEEP_TEAL }),
          ...[
            { n: '01', title: 'Kickoff call', when: 'Wed, July 15 · 10:00 AM PT', body: 'Goals, roles, and the 90-day roadmap. The invite is already in your inbox.' },
            { n: '02', title: 'Access & audits', when: 'July 15 – 22', body: 'We connect analytics, ads, and Shopify, then baseline everything we will improve.' },
            { n: '03', title: 'Strategy readout', when: 'Wed, July 29', body: 'You get the plan, the targets, and the first-quarter marketing calendar.' },
          ].flatMap((step, i) => {
            const left = 64 + i * 235;
            return [
              rect({ name: `Step ${step.n} Card`, left, top: 376, w: 218, h: 158, fill: CARD, radius: 8 }),
              rect({ name: `Step ${step.n} Tick`, left, top: 376, w: 3, h: 158, fill: DEEP_TEAL }),
              text({ name: `Step ${step.n} Number`, left: left + 18, top: 392, width: 60, text: step.n, size: 18, weight: 800, color: DEEP_TEAL }),
              text({ name: `Step ${step.n} Title`, left: left + 18, top: 420, width: 184, text: step.title, size: 12.5, weight: 700, color: INK }),
              text({ name: `Step ${step.n} When`, left: left + 18, top: 442, width: 184, text: step.when, size: 9, weight: 600, color: MUTED }),
              para({ name: `Step ${step.n} Body`, left: left + 18, top: 462, width: 184, height: 58, text: step.body, size: 9.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
            ];
          }),
          rect({ name: 'Today Card', left: M, top: 574, w: CW, h: 92, fill: '#e6f2f0', radius: 8 }),
          rect({ name: 'Today Tick', left: M, top: 574, w: 3, h: 92, fill: DEEP_TEAL }),
          text({ name: 'Today Label', left: M + 24, top: 592, width: 400, text: 'ONE THING TO DO TODAY', size: 8.5, weight: 700, color: DEEP_TEAL, tracking: 1.4 }),
          para({ name: 'Today Body', left: M + 24, top: 612, width: 640, height: 40, text: 'Accept the Slack Connect invite and add anyone who should see weekly updates — that channel is where the fast answers live.', size: 11, weight: 500, color: '#134e4a', lineHeight: 1.55 }),
          rect({ name: 'AD Card', left: M, top: 706, w: 336, h: 110, fill: CARD, radius: 8 }),
          text({ name: 'AD Label', left: M + 20, top: 724, width: 296, text: 'YOUR ACCOUNT DIRECTOR', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          lines({ name: 'AD Detail', left: M + 20, top: 746, width: 296, text: 'Sasha Nguyen\nsasha@atlascreative.co · (415) 555-0177', size: 10.5, weight: 500, color: BODY_COLOR, lineHeight: 1.7 }),
          rect({ name: 'Urgent Card', left: 416, top: 706, w: 336, h: 110, fill: CARD, radius: 8 }),
          text({ name: 'Urgent Label', left: 436, top: 724, width: 296, text: 'ANYTHING URGENT', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          lines({ name: 'Urgent Detail', left: 436, top: 746, width: 296, text: 'Slack — #juniper-atlas\nReplies inside 2 business hours', size: 10.5, weight: 500, color: BODY_COLOR, lineHeight: 1.7 }),
          rect({ name: 'Welcome Footer Rule', left: M, top: 976, w: CW, h: 1, fill: RULE }),
          text({ name: 'Welcome Footer Left', left: M, top: 990, width: 420, text: 'Juniper Home Goods · Welcome Packet · July 2026', size: 9, weight: 500, color: FAINT }),
          text({ name: 'Welcome Footer Right', left: PAGE_W - M - 160, top: 990, width: 160, text: 'Page 1 of 4', size: 9, weight: 500, color: FAINT, align: 'right' }),
        ],
      },
      {
        label: 'Team & Communication',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: DEEP_TEAL, docLabel: 'Welcome Packet — Juniper Home Goods', footerLeft: 'Atlas Creative Studio · Welcome Packet', page: 2, total: 4 }),
          ...pageTitle({ kicker: 'Your team', title: 'Three people, no account shuffle', sub: 'The team below is your team for the whole engagement — no handoffs to juniors.', accent: DEEP_TEAL }).layers,
          ...[
            { init: 'SN', name: 'Sasha Nguyen', role: 'Account Director', email: 'sasha@atlascreative.co', focus: 'Strategy, budget, escalations' },
            { init: 'MW', name: 'Marcus Webb', role: 'Design Lead', email: 'marcus@atlascreative.co', focus: 'Brand, site & creative work' },
            { init: 'PP', name: 'Priya Patel', role: 'Analytics Lead', email: 'priya@atlascreative.co', focus: 'Tracking, reporting, experiments' },
          ].flatMap((member, i) => {
            const left = 64 + i * 235;
            return [
              rect({ name: `${member.init} Card`, left, top: 200, w: 218, h: 158, fill: CARD, radius: 8 }),
              rect({ name: `${member.init} Avatar`, left: left + 18, top: 218, w: 40, h: 40, fill: DEEP_TEAL, radius: 20 }),
              text({ name: `${member.init} Initials`, left: left + 18, top: 230, width: 40, text: member.init, size: 13, weight: 700, color: '#ffffff', align: 'center' }),
              text({ name: `${member.init} Name`, left: left + 18, top: 268, width: 184, text: member.name, size: 12.5, weight: 700, color: INK }),
              text({ name: `${member.init} Role`, left: left + 18, top: 288, width: 184, text: member.role.toUpperCase(), size: 8.5, weight: 700, color: DEEP_TEAL, tracking: 1 }),
              text({ name: `${member.init} Email`, left: left + 18, top: 308, width: 184, text: member.email, size: 9, weight: 500, color: MUTED }),
              text({ name: `${member.init} Focus`, left: left + 18, top: 328, width: 184, text: member.focus, size: 9.5, weight: 500, color: BODY_COLOR }),
            ];
          }),
          ...sectionLabel({ label: 'Where conversations live', top: 398, accent: DEEP_TEAL }),
          ...table({
            name: 'Comms',
            top: 434,
            cols: [
              { label: 'Channel', left: 80, width: 160 },
              { label: 'Use it for', left: 280, width: 260 },
              { label: 'Response time', left: 576, width: 160, align: 'right' },
            ],
            rows: [
              ['Slack Connect', 'Quick questions & approvals', 'Under 2 business hrs'],
              ['Email', 'Decisions & written summaries', 'Under 1 business day'],
              ['Weekly call', 'Priorities, blockers, demos', 'Tuesdays 9:30 AM PT'],
              ['Client portal', 'Files, invoices, reports', 'Always current'],
            ],
            headFill: DEEP_TEAL,
            strongCol: 0,
          }).layers,
          ...sectionLabel({ label: 'Meeting cadence', top: 630, accent: DEEP_TEAL }),
          ...[
            'Weekly — 25-minute working call, Tuesdays 9:30 AM PT (recurring invite sent)',
            'Monthly — performance report lands the first Friday, walkthrough on the next call',
            'Quarterly — 90-minute roadmap review, in person or video',
          ].flatMap((cadenceLine, i) => [
            rect({ name: `Cadence ${i + 1} Bullet`, left: M, top: 671 + i * 28, w: 5, h: 5, fill: DEEP_TEAL }),
            text({ name: `Cadence ${i + 1}`, left: 80, top: 666 + i * 28, width: 660, text: cadenceLine, size: 11, weight: 500, color: BODY_COLOR }),
          ]),
          rect({ name: 'Escalation Card', left: M, top: 776, w: CW, h: 96, fill: '#e6f2f0', radius: 8 }),
          rect({ name: 'Escalation Tick', left: M, top: 776, w: 3, h: 96, fill: DEEP_TEAL }),
          text({ name: 'Escalation Label', left: M + 24, top: 794, width: 400, text: 'IF SOMETHING FEELS OFF', size: 8.5, weight: 700, color: DEEP_TEAL, tracking: 1.4 }),
          para({ name: 'Escalation Body', left: M + 24, top: 814, width: 640, height: 44, text: 'Tell Sasha first. If it is not visibly moving within 48 hours, email jordan@atlascreative.co — the principal reads every escalation the same day.', size: 11, weight: 500, color: '#134e4a', lineHeight: 1.55 }),
        ],
      },
      {
        label: 'First 90 Days',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: DEEP_TEAL, docLabel: 'Welcome Packet — Juniper Home Goods', footerLeft: 'Atlas Creative Studio · Welcome Packet', page: 3, total: 4 }),
          ...pageTitle({ kicker: 'Roadmap', title: 'The first 90 days, mapped', accent: DEEP_TEAL }).layers,
          ...table({
            name: 'Roadmap',
            top: 180,
            cols: [
              { label: 'Weeks', left: 80, width: 80 },
              { label: 'Focus', left: 200, width: 240 },
              { label: 'You will see', left: 470, width: 266, align: 'right' },
            ],
            rows: [
              ['1–2', 'Onboarding & baselines', 'Kickoff, access, audit findings'],
              ['3–6', 'Quick wins', 'Fixes shipped weekly + first tests'],
              ['7–10', 'Build the engine', 'Email flows, landing pages, creative'],
              ['11–13', 'Review & double down', 'QBR with results and next quarter'],
            ],
            headFill: DEEP_TEAL,
            rowH: 38,
            strongCol: 1,
          }).layers,
          ...sectionLabel({ label: 'What we need from you', top: 412, accent: DEEP_TEAL }),
          ...[
            { item: 'Shopify collaborator access', due: 'July 16' },
            { item: 'GA4 and Google Ads admin invites', due: 'July 16' },
            { item: 'Brand book and asset folder', due: 'July 20' },
            { item: 'Product margin ranges by category', due: 'July 24' },
            { item: 'Past creative performance exports', due: 'July 27' },
          ].flatMap((check, i) => {
            const y = 452 + i * 40;
            return [
              rect({ name: `Check ${i + 1} Box`, left: M, top: y, w: 14, h: 14, fill: '#cfe6e2', radius: 3 }),
              text({ name: `Check ${i + 1} Item`, left: 96, top: y - 2, width: 460, text: check.item, size: 11.5, weight: 500, color: INK }),
              text({ name: `Check ${i + 1} Due`, left: PAGE_W - M - 120, top: y - 1, width: 120, text: check.due, size: 10, weight: 600, color: MUTED, align: 'right' }),
              ...(i < 4 ? [rect({ name: `Check ${i + 1} Rule`, left: M, top: y + 26, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          ...sectionLabel({ label: 'How we measure the quarter', top: 682, accent: DEEP_TEAL }),
          ...kpiCard({ name: 'Measure RPS', left: 64, top: 718, w: 218, h: 96, label: 'Revenue / session', value: '+15%', delta: 'North-star metric', deltaColor: MUTED, accent: DEEP_TEAL }),
          ...kpiCard({ name: 'Measure Email', left: 299, top: 718, w: 218, h: 96, label: 'Email revenue share', value: '12% → 18%', delta: 'Flows + campaigns', deltaColor: MUTED, accent: DEEP_TEAL, valueSize: 19 }),
          ...kpiCard({ name: 'Measure ROAS', left: 534, top: 718, w: 218, h: 96, label: 'Blended ROAS', value: '≥ 4.5x', delta: 'Floor, not a ceiling', deltaColor: MUTED, accent: DEEP_TEAL }),
          text({ name: 'Roadmap Note', left: M, top: 852, width: CW, text: 'Targets are confirmed together at the strategy readout on July 29 — nothing here is set in stone yet.', size: 9.5, weight: 500, color: FAINT, italic: true }),
        ],
      },
      {
        label: 'Tools, Access & FAQ',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: DEEP_TEAL, docLabel: 'Welcome Packet — Juniper Home Goods', footerLeft: 'Atlas Creative Studio · Welcome Packet', page: 4, total: 4 }),
          ...pageTitle({ kicker: 'Logistics', title: 'Tools, access & good answers', accent: DEEP_TEAL }).layers,
          ...table({
            name: 'Access',
            top: 180,
            cols: [
              { label: 'Tool', left: 80, width: 150 },
              { label: 'What it is for', left: 270, width: 280 },
              { label: 'Who grants it', left: 586, width: 150, align: 'right' },
            ],
            rows: [
              ['GA4', 'Traffic and conversion truth', 'Juniper IT'],
              ['Google Ads', 'Paid search management', 'Juniper IT'],
              ['Meta Business', 'Paid social management', 'Juniper marketing'],
              ['Shopify', 'Store, apps, checkout', 'Juniper ops'],
              ['Klaviyo', 'Email & SMS flows', 'Atlas sets up'],
            ],
            headFill: DEEP_TEAL,
            strongCol: 0,
          }).layers,
          ...sectionLabel({ label: 'Quick answers', top: 440, accent: DEEP_TEAL }),
          ...[
            { q: 'Who owns the accounts and the data?', a: 'You do. Everything lives in Juniper-owned accounts; we are added as managers and leave clean if we ever part ways.' },
            { q: 'How do change requests work?', a: 'Anything under two hours we just do. Bigger items get a same-week estimate in Slack before any work starts.' },
            { q: 'When do invoices arrive?', a: 'Retainer invoices land on the 1st, due net 15. The client portal keeps full history and receipts.' },
          ].flatMap((faq, i) => {
            const y = 480 + i * 88;
            return [
              text({ name: `FAQ ${i + 1} Q`, left: M, top: y, width: CW, text: faq.q, size: 12, weight: 700, color: INK }),
              para({ name: `FAQ ${i + 1} A`, left: M, top: y + 22, width: CW, height: 38, text: faq.a, size: 10.5, weight: 400, color: BODY_COLOR, lineHeight: 1.55 }),
              ...(i < 2 ? [rect({ name: `FAQ ${i + 1} Rule`, left: M, top: y + 68, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          rect({ name: 'Closing Banner', left: M, top: 764, w: CW, h: 108, fill: DEEP_TEAL, radius: 10 }),
          text({ name: 'Closing Line', left: M + 28, top: 790, width: 632, text: 'That is it — see you Wednesday at kickoff.', size: 16, weight: 700, color: '#ffffff' }),
          text({ name: 'Closing Contacts', left: M + 28, top: 822, width: 632, text: '#juniper-atlas on Slack · sasha@atlascreative.co · (415) 555-0177', size: 10.5, weight: 500, color: '#bfe3dd' }),
        ],
      },
    ],
  },

  /* ════════════════ 8. CASE STUDY — terracotta ════════════════ */
  {
    id: 'case-study-pack',
    title: 'Client Case Study',
    shortLabel: 'Case Study',
    description: 'Four-page results story: headline outcome cover, challenge and approach, charted quarter-by-quarter results, and lessons with an engagement summary.',
    icon: 'fas fa-trophy',
    composerPresetId: 'doc-case-study',
    pages: [
      {
        label: 'Cover',
        ...LETTER_PAGE,
        layers: [
          rect({ name: 'Top Accent Bar', left: 0, top: 0, w: PAGE_W, h: 6, fill: TERRACOTTA }),
          text({ name: 'Studio Wordmark', left: M, top: 40, width: 400, text: 'ATLAS CREATIVE STUDIO', size: 10, weight: 700, color: MUTED, tracking: 2.2 }),
          text({ name: 'Case Study Eyebrow', left: PAGE_W - M - 280, top: 40, width: 280, text: 'CASE STUDY · E-COMMERCE', size: 10, weight: 700, color: TERRACOTTA, align: 'right', tracking: 2 }),
          text({ name: 'Headline Stat', left: M, top: 108, width: 640, text: '+162%', size: 56, weight: 800, color: TERRACOTTA }),
          text({ name: 'Headline Context', left: M, top: 186, width: 640, text: 'Online revenue growth in nine months', size: 19, weight: 700, color: INK }),
          text({ name: 'Engagement Meta', left: M, top: 218, width: 640, text: 'Redwood Cycling Co. × Atlas Creative Studio · October 2025 – June 2026', size: 11, weight: 500, color: MUTED }),
          rect({ name: 'Cover Rule', left: M, top: 254, w: CW, h: 1, fill: RULE }),
          ...kpiCard({ name: 'Cover Stat Organic', left: 64, top: 286, w: 218, h: 100, label: 'Organic sessions', value: '+138%', delta: '48K → 114K per month', deltaColor: MUTED, accent: TERRACOTTA }),
          ...kpiCard({ name: 'Cover Stat ROAS', left: 299, top: 286, w: 218, h: 100, label: 'Blended ROAS', value: '5.8x', delta: 'Up from 3.1x baseline', deltaColor: MUTED, accent: TERRACOTTA }),
          ...kpiCard({ name: 'Cover Stat Email', left: 534, top: 286, w: 218, h: 100, label: 'Email revenue share', value: '9% → 22%', delta: 'Klaviyo flows + campaigns', deltaColor: MUTED, accent: TERRACOTTA, valueSize: 19 }),
          ...sectionLabel({ label: 'The one-paragraph version', top: 430, accent: TERRACOTTA }),
          para({ name: 'Summary Paragraph', left: M, top: 466, width: CW, height: 82, text: 'Redwood Cycling makes premium gravel frames with a cult following — and had a website that could not carry it. Over nine months we rebuilt the store, moved paid spend from prospecting sprawl to a tight full-funnel program, and turned email into the second-largest channel. Monthly online revenue went from $86,000 to $225,000 with margin intact.', size: 12, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          rect({ name: 'Quote Card', left: M, top: 580, w: CW, h: 122, fill: '#f8ece5', radius: 8 }),
          rect({ name: 'Quote Tick', left: M, top: 580, w: 3, h: 122, fill: TERRACOTTA }),
          para({ name: 'Founder Quote', left: M + 24, top: 598, width: 616, height: 52, text: '“The site finally sells the way our bikes ride. Atlas ran this like a race team — every week had a split time and a decision.”', size: 14, weight: 400, color: '#7c2d12', family: SERIF, italic: true, lineHeight: 1.55 }),
          text({ name: 'Quote Attribution', left: M + 24, top: 662, width: 600, text: 'Casey Lombard — Founder, Redwood Cycling Co.', size: 10, weight: 600, color: MUTED }),
          ...sectionLabel({ label: 'What we ran', top: 742, accent: TERRACOTTA }),
          text({ name: 'Services Line', left: M, top: 778, width: CW, text: 'Storefront rebuild · Paid search & social · Lifecycle email · Analytics & experimentation', size: 11.5, weight: 500, color: BODY_COLOR }),
          rect({ name: 'Cover Footer Rule', left: M, top: 976, w: CW, h: 1, fill: RULE }),
          text({ name: 'Cover Footer Left', left: M, top: 990, width: 420, text: 'Case study · Redwood Cycling Co.', size: 9, weight: 500, color: FAINT }),
          text({ name: 'Cover Footer Right', left: PAGE_W - M - 160, top: 990, width: 160, text: 'Page 1 of 4', size: 9, weight: 500, color: FAINT, align: 'right' }),
        ],
      },
      {
        label: 'Challenge & Approach',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TERRACOTTA, docLabel: 'Case Study — Redwood Cycling Co.', footerLeft: 'Atlas Creative Studio · Case Study', page: 2, total: 4 }),
          ...pageTitle({ kicker: '01 · The challenge', title: 'A brand outgrowing its store', accent: TERRACOTTA }).layers,
          para({ name: 'Challenge Paragraph 1', left: M, top: 184, width: CW, height: 60, text: 'By late 2025 Redwood’s frames were reviewed everywhere and back-ordered twice a year — yet online revenue had been flat for four quarters. Mobile converted at 0.9%, pages took six seconds to load, and paid spend leaned on one prospecting audience that had quietly fatigued.', size: 12, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          para({ name: 'Challenge Paragraph 2', left: M, top: 250, width: CW, height: 42, text: 'The team knew the numbers were soft; what they needed was a sequence — which fix first, what to measure, and when to push spend.', size: 12, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          ...sectionLabel({ label: 'Where it stood', top: 316, accent: TERRACOTTA }),
          ...kpiCard({ name: 'Before Conversion', left: 64, top: 352, w: 218, h: 92, label: 'Mobile conversion', value: '0.9%', delta: 'Industry median — 1.8%', deltaColor: MUTED, accent: TERRACOTTA }),
          ...kpiCard({ name: 'Before Speed', left: 299, top: 352, w: 218, h: 92, label: 'Page load (LCP)', value: '6.1s', delta: 'On 4G mobile', deltaColor: MUTED, accent: TERRACOTTA }),
          ...kpiCard({ name: 'Before Email', left: 534, top: 352, w: 218, h: 92, label: 'Email revenue', value: '9%', delta: 'One template, no flows', deltaColor: MUTED, accent: TERRACOTTA }),
          ...pageTitle({ kicker: '02 · The approach', title: 'Three workstreams, one scorecard', accent: TERRACOTTA, top: 496 }).layers,
          ...[
            { n: '01', title: 'Rebuild the store', body: 'New Shopify theme built for speed and product storytelling: sub-two-second pages, configurator-style PDPs, and a two-step checkout.', tag: 'Months 1–3' },
            { n: '02', title: 'Restructure paid', body: 'Collapsed 40 ad sets into a full-funnel program with creative testing on a weekly cadence and spend gated by contribution margin.', tag: 'Months 2–5' },
            { n: '03', title: 'Turn on lifecycle', body: 'Seven Klaviyo flows plus two campaigns a week — restock alerts alone recovered $18K in their first month.', tag: 'Months 3–9' },
          ].flatMap((ws, i) => {
            const y = 630 + i * 96;
            return [
              rect({ name: `Workstream ${ws.n} Chip`, left: M, top: y, w: 40, h: 22, fill: TERRACOTTA, radius: 11 }),
              text({ name: `Workstream ${ws.n} Chip Text`, left: M, top: y + 5, width: 40, text: ws.n, size: 9.5, weight: 700, color: '#ffffff', align: 'center' }),
              text({ name: `Workstream ${ws.n} Title`, left: 130, top: y, width: 420, text: ws.title, size: 13.5, weight: 700, color: INK }),
              para({ name: `Workstream ${ws.n} Body`, left: 130, top: y + 24, width: 440, height: 40, text: ws.body, size: 10.5, weight: 400, color: BODY_COLOR, lineHeight: 1.5 }),
              text({ name: `Workstream ${ws.n} Tag`, left: 620, top: y + 2, width: 132, text: ws.tag, size: 9.5, weight: 600, color: MUTED, align: 'right' }),
              ...(i < 2 ? [rect({ name: `Workstream ${ws.n} Rule`, left: M, top: y + 76, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
        ],
      },
      {
        label: 'Results',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TERRACOTTA, docLabel: 'Case Study — Redwood Cycling Co.', footerLeft: 'Atlas Creative Studio · Case Study', page: 3, total: 4 }),
          ...pageTitle({ kicker: '03 · Results', title: 'Quarter by quarter, up and to the right', accent: TERRACOTTA }).layers,
          graph({
            name: 'Revenue by Quarter',
            left: M,
            top: 190,
            w: CW,
            h: 270,
            kind: 'bar',
            bindings: [
              { label: 'Baseline (Sep)', fallbackValue: 86, fill: solid('#94a3b8') },
              { label: 'Oct – Dec', fallbackValue: 94, fill: solid('#d9906b') },
              { label: 'Jan – Mar', fallbackValue: 148, fill: solid('#c2622f') },
              { label: 'Apr – Jun', fallbackValue: 225, fill: solid(TERRACOTTA) },
            ],
            options: {
              orientation: 'vertical',
              growDirection: 'positive',
              minValue: 0,
              maxValue: 250,
              barSpacing: 0.42,
              cornerRadius: 4,
              seriesLabels: { mode: 'text', position: 'bottom', fontSize: 10, color: '#64748b', gap: 8 },
              valueLabels: { mode: 'above', fontSize: 10, color: '#334155', decimals: 0, prefix: '$', suffix: 'K' },
            },
          }),
          text({ name: 'Revenue Chart Caption', left: M, top: 496, width: CW, text: 'Average monthly online revenue, in thousands of dollars.', size: 9, weight: 500, color: FAINT, italic: true }),
          graph({
            name: 'Organic Sessions Trend',
            left: M,
            top: 520,
            w: CW,
            h: 190,
            kind: 'line',
            bindings: [
              {
                label: 'Organic sessions (K)',
                fill: solid(TERRACOTTA),
                segments: [
                  { label: 'Oct', fallbackValue: 48 },
                  { label: 'Dec', fallbackValue: 61 },
                  { label: 'Feb', fallbackValue: 74 },
                  { label: 'Apr', fallbackValue: 92 },
                  { label: 'Jun', fallbackValue: 114 },
                ],
              },
            ],
            options: { curve: 'smooth', lineWidth: 3, showDataPoints: true, dataPointSize: 6, minValue: 30, maxValue: 130, legend: { enabled: false } },
          }),
          text({ name: 'Sessions Chart Caption', left: M, top: 724, width: CW, text: 'Monthly organic sessions in thousands, October 2025 through June 2026.', size: 9, weight: 500, color: FAINT, italic: true }),
          rect({ name: 'Results Insight Card', left: M, top: 782, w: CW, h: 96, fill: '#f8ece5', radius: 8 }),
          rect({ name: 'Results Insight Tick', left: M, top: 782, w: 3, h: 96, fill: TERRACOTTA }),
          text({ name: 'Results Insight Label', left: M + 24, top: 800, width: 300, text: 'THE COMPOUNDING PART', size: 8.5, weight: 700, color: TERRACOTTA, tracking: 1.4 }),
          para({ name: 'Results Insight Body', left: M + 24, top: 820, width: 640, height: 44, text: 'Faster pages lifted every channel at once: paid CPAs fell 31% with no bid changes, and organic gains stacked on top — the rebuild paid for itself before the paid program even scaled.', size: 11, weight: 500, color: '#7c2d12', lineHeight: 1.55 }),
        ],
      },
      {
        label: 'Lessons & Engagement',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: TERRACOTTA, docLabel: 'Case Study — Redwood Cycling Co.', footerLeft: 'Atlas Creative Studio · Case Study', page: 4, total: 4 }),
          ...pageTitle({ kicker: '04 · What made it work', title: 'Three habits worth stealing', accent: TERRACOTTA }).layers,
          ...[
            { title: 'Weekly split times', body: 'Every experiment carried a number and a deadline; anything without both got cut in planning.' },
            { title: 'Margin-gated spend', body: 'Paid budgets scaled only when contribution margin held — growth that costs the bottom line is churn in disguise.' },
            { title: 'One scorecard', body: 'Founder, studio, and media team read the same five numbers every Monday. No competing dashboards, no surprise narratives.' },
          ].flatMap((lesson, i) => {
            const y = 192 + i * 78;
            return [
              rect({ name: `Lesson ${i + 1} Bullet`, left: M, top: y + 6, w: 6, h: 6, fill: TERRACOTTA }),
              text({ name: `Lesson ${i + 1} Title`, left: 88, top: y, width: 620, text: lesson.title, size: 13, weight: 700, color: INK }),
              para({ name: `Lesson ${i + 1} Body`, left: 88, top: y + 22, width: 620, height: 38, text: lesson.body, size: 10.5, weight: 400, color: BODY_COLOR, lineHeight: 1.5 }),
            ];
          }),
          ...sectionLabel({ label: 'Engagement summary', top: 448, accent: TERRACOTTA }),
          ...table({
            name: 'Engagement',
            top: 484,
            cols: [
              { label: 'Detail', left: 80, width: 180 },
              { label: 'Summary', left: 300, width: 436 },
            ],
            rows: [
              ['Timeline', 'October 2025 – June 2026, now an ongoing retainer'],
              ['Services', 'Storefront rebuild · paid media · lifecycle email · analytics'],
              ['Team', 'Three senior leads — no handoffs, no account layers'],
              ['Stack', 'Shopify Plus · Klaviyo · GA4 · Meta & Google Ads'],
            ],
            headFill: TERRACOTTA,
            strongCol: 0,
          }).layers,
          ...sectionLabel({ label: 'About Atlas', top: 680, accent: TERRACOTTA }),
          para({ name: 'About Atlas Body', left: M, top: 716, width: CW, height: 58, text: 'Atlas Creative Studio is a senior design and growth team for consumer brands: 87 projects shipped, an average 6.2x blended ROAS across active retainers, and a 4.9/5 client rating over eleven years.', size: 11.5, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
          rect({ name: 'CTA Banner', left: M, top: 800, w: CW, h: 96, fill: TERRACOTTA, radius: 10 }),
          text({ name: 'CTA Line', left: M + 28, top: 824, width: 632, text: 'Ready for a season like this one?', size: 15, weight: 700, color: '#ffffff' }),
          text({ name: 'CTA Contacts', left: M + 28, top: 852, width: 632, text: 'hello@atlascreative.co · atlascreative.co/work · (415) 555-0134', size: 10.5, weight: 500, color: '#fbe3d4' }),
        ],
      },
    ],
  },

  /* ════════════════ 9. MEETING MINUTES — petrol ════════════════ */
  {
    id: 'meeting-minutes-pack',
    title: 'Meeting Minutes & Action Log',
    shortLabel: 'Meeting Minutes',
    description: 'Three-page minutes: meeting header with attendees and timed agenda, discussion highlights with a decision log, and a prioritized action-item table.',
    icon: 'fas fa-clipboard-list',
    composerPresetId: 'doc-meeting-minutes',
    pages: [
      {
        label: 'Meeting & Agenda',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: PETROL, docLabel: 'Meeting Minutes — Project Meridian', footerLeft: 'Atlas Creative Studio · Sprint 6 Review Minutes', page: 1, total: 3 }),
          ...pageTitle({ kicker: 'Meeting minutes', title: 'Sprint 6 review — Project Meridian', sub: 'Thursday, July 9, 2026 · 10:00–10:45 AM PT · Zoom', accent: PETROL }).layers,
          ...kpiCard({ name: 'Meta Facilitator', left: 64, top: 200, w: 218, h: 88, label: 'Facilitator', value: 'Jordan R.', delta: 'Atlas — Principal', deltaColor: MUTED, accent: PETROL, valueSize: 18 }),
          ...kpiCard({ name: 'Meta Notes', left: 299, top: 200, w: 218, h: 88, label: 'Notes', value: 'Priya P.', delta: 'Circulated same day', deltaColor: MUTED, accent: PETROL, valueSize: 18 }),
          ...kpiCard({ name: 'Meta Status', left: 534, top: 200, w: 218, h: 88, label: 'Project status', value: 'On track', delta: 'Launch — Oct 23', accent: PETROL, valueSize: 18 }),
          ...sectionLabel({ label: 'Attendees', top: 328, accent: PETROL }),
          ...table({
            name: 'Attendees',
            top: 364,
            cols: [
              { label: 'Name', left: 80, width: 200 },
              { label: 'Role', left: 320, width: 220 },
              { label: 'Organization', left: 576, width: 160, align: 'right' },
            ],
            rows: [
              ['Dana Whitfield', 'VP Marketing', 'Meridian Outdoor'],
              ['Tom Okafor', 'E-commerce Manager', 'Meridian Outdoor'],
              ['Jordan Reyes', 'Principal', 'Atlas'],
              ['Marcus Webb', 'Design Lead', 'Atlas'],
              ['Priya Patel', 'Analytics Lead', 'Atlas'],
            ],
            headFill: PETROL,
            rowH: 32,
            strongCol: 0,
          }).layers,
          ...sectionLabel({ label: 'Agenda', top: 588, accent: PETROL }),
          ...[
            { n: '1', item: 'Sprint 6 demo — rebuilt checkout flow', time: '15 min' },
            { n: '2', item: 'Content migration status & blockers', time: '10 min' },
            { n: '3', item: 'Launch-week staffing plan', time: '10 min' },
            { n: '4', item: 'Risks, parking lot, and next steps', time: '10 min' },
          ].flatMap((agendaRow, i) => {
            const y = 628 + i * 44;
            return [
              rect({ name: `Agenda ${agendaRow.n} Chip`, left: M, top: y, w: 24, h: 24, fill: '#e7f1f5', radius: 12 }),
              text({ name: `Agenda ${agendaRow.n} Number`, left: M, top: y + 6, width: 24, text: agendaRow.n, size: 10.5, weight: 700, color: PETROL, align: 'center' }),
              text({ name: `Agenda ${agendaRow.n} Item`, left: 104, top: y + 4, width: 480, text: agendaRow.item, size: 12, weight: 600, color: INK }),
              text({ name: `Agenda ${agendaRow.n} Time`, left: PAGE_W - M - 100, top: y + 6, width: 100, text: agendaRow.time, size: 10, weight: 600, color: MUTED, align: 'right' }),
              ...(i < 3 ? [rect({ name: `Agenda ${agendaRow.n} Rule`, left: M, top: y + 34, w: CW, h: 1, fill: RULE })] : []),
            ];
          }),
          rect({ name: 'Distribution Card', left: M, top: 828, w: CW, h: 80, fill: CARD, radius: 8 }),
          text({ name: 'Distribution Label', left: M + 24, top: 846, width: 400, text: 'DISTRIBUTION', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          text({ name: 'Distribution Body', left: M + 24, top: 868, width: 640, text: 'All attendees + exec sponsor (K. Mercer). Minutes stored in the shared drive under /meridian/minutes.', size: 10.5, weight: 400, color: BODY_COLOR }),
        ],
      },
      {
        label: 'Discussion & Decisions',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: PETROL, docLabel: 'Meeting Minutes — Project Meridian', footerLeft: 'Atlas Creative Studio · Sprint 6 Review Minutes', page: 2, total: 3 }),
          ...pageTitle({ kicker: '02 · Discussion', title: 'What we covered', accent: PETROL }).layers,
          ...[
            { title: 'Checkout demo', body: 'The rebuilt three-step checkout tested 28% faster than production in the demo environment. One open defect: Apple Pay fails on the PO-box edge case — fix is scoped and committed to Sprint 7.' },
            { title: 'Content migration', body: '96 of 140 pages are migrated. Product-care articles are running slower than planned because source formatting is inconsistent; roughly two extra days of effort, absorbed inside the sprint buffer.' },
            { title: 'Launch-week staffing', body: 'Meridian confirms two support agents for launch week. Atlas provides an on-call engineer for the first 72 hours after cutover, with a shared incident channel opening October 21.' },
          ].flatMap((topic, i) => {
            const y = 184 + i * 118;
            return [
              rect({ name: `Topic ${i + 1} Tick`, left: M, top: y + 4, w: 3, h: 40, fill: PETROL }),
              text({ name: `Topic ${i + 1} Title`, left: 84, top: y, width: 620, text: topic.title, size: 13.5, weight: 700, color: INK }),
              para({ name: `Topic ${i + 1} Body`, left: 84, top: y + 26, width: 648, height: 58, text: topic.body, size: 11, weight: 400, color: BODY_COLOR, lineHeight: 1.6 }),
            ];
          }),
          ...sectionLabel({ label: 'Decisions made', top: 556, accent: PETROL }),
          ...table({
            name: 'Decisions',
            top: 592,
            cols: [
              { label: 'Ref', left: 80, width: 50 },
              { label: 'Decision', left: 150, width: 400 },
              { label: 'Made by', left: 570, width: 90 },
              { label: 'Date', left: 676, width: 60, align: 'right' },
            ],
            rows: [
              ['D-14', 'Launch date holds — Friday, October 23', 'Dana W.', 'Jul 9'],
              ['D-15', 'Apple Pay defect is launch-blocking; verified fix due Oct 9', 'Group', 'Jul 9'],
              ['D-16', 'Legacy blog comments will not migrate', 'Tom O.', 'Jul 9'],
            ],
            headFill: PETROL,
            rowH: 38,
            strongCol: 1,
          }).layers,
          rect({ name: 'Risk Card', left: M, top: 776, w: CW, h: 104, fill: '#e7f1f5', radius: 8 }),
          rect({ name: 'Risk Tick', left: M, top: 776, w: 3, h: 104, fill: PETROL }),
          text({ name: 'Risk Label', left: M + 24, top: 794, width: 400, text: 'RISK WATCH', size: 8.5, weight: 700, color: PETROL, tracking: 1.4 }),
          para({ name: 'Risk Body', left: M + 24, top: 814, width: 640, height: 52, text: 'Peak-season shipping cutoffs compress the launch window: if the Apple Pay fix slips past October 9, the fallback date is November 6 — after the holiday code freeze it would be January. Decision point is the October 2 call.', size: 10.5, weight: 500, color: '#0c4a6e', lineHeight: 1.55 }),
        ],
      },
      {
        label: 'Action Items',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: PETROL, docLabel: 'Meeting Minutes — Project Meridian', footerLeft: 'Atlas Creative Studio · Sprint 6 Review Minutes', page: 3, total: 3 }),
          ...pageTitle({ kicker: '03 · Actions', title: 'Who does what by when', accent: PETROL }).layers,
          ...table({
            name: 'Actions',
            top: 180,
            cols: [
              { label: 'Ref', left: 80, width: 50 },
              { label: 'Action', left: 150, width: 330 },
              { label: 'Owner', left: 500, width: 90 },
              { label: 'Due', left: 606, width: 60, align: 'right' },
              { label: 'Priority', left: 682, width: 54, align: 'right' },
            ],
            rows: [
              ['A-41', 'Fix Apple Pay PO-box defect + regression test', 'Priya P.', 'Jul 16', 'High'],
              ['A-42', 'Deliver remaining product-care source docs', 'Tom O.', 'Jul 14', 'High'],
              ['A-43', 'Draft launch-week comms plan for support', 'Dana W.', 'Jul 21', 'Med'],
              ['A-44', 'Publish redirect map for legal review', 'Marcus W.', 'Jul 17', 'Med'],
              ['A-45', 'Schedule cutover rehearsal on staging', 'Jordan R.', 'Jul 23', 'Med'],
              ['A-46', 'Confirm on-call rotation for Oct 23–26', 'Jordan R.', 'Jul 30', 'Low'],
            ],
            headFill: PETROL,
            rowH: 36,
            strongCol: 1,
          }).layers,
          ...sectionLabel({ label: 'Parking lot', top: 480, accent: PETROL }),
          ...[
            'Loyalty program scope — revisit after launch (owner: Dana).',
            'Wholesale portal request from the sales team — needs a discovery estimate first.',
          ].flatMap((parkingLine, i) => [
            rect({ name: `Parking ${i + 1} Bullet`, left: M, top: 521 + i * 28, w: 5, h: 5, fill: PETROL }),
            text({ name: `Parking ${i + 1}`, left: 80, top: 516 + i * 28, width: 660, text: parkingLine, size: 11, weight: 500, color: BODY_COLOR }),
          ]),
          rect({ name: 'Next Meeting Card', left: M, top: 604, w: 336, h: 110, fill: '#e7f1f5', radius: 8 }),
          text({ name: 'Next Meeting Label', left: M + 20, top: 622, width: 296, text: 'NEXT MEETING', size: 8.5, weight: 700, color: PETROL, tracking: 1.4 }),
          lines({ name: 'Next Meeting Detail', left: M + 20, top: 644, width: 296, text: 'Sprint 7 review\nThursday, July 23 · 10:00 AM PT\nSame Zoom link — recurring invite', size: 10.5, weight: 500, color: '#0c4a6e', lineHeight: 1.65 }),
          rect({ name: 'Approval Card', left: 416, top: 604, w: 336, h: 110, fill: CARD, radius: 8 }),
          text({ name: 'Approval Label', left: 436, top: 622, width: 296, text: 'MINUTES APPROVAL', size: 8.5, weight: 700, color: MUTED, tracking: 1.4 }),
          rect({ name: 'Approval Line', left: 436, top: 668, w: 296, h: 1, fill: FAINT }),
          text({ name: 'Approval Caption', left: 436, top: 676, width: 296, text: 'Approved by · date', size: 8.5, weight: 500, color: FAINT }),
          para({ name: 'Minutes Note', left: M, top: 748, width: CW, height: 34, text: 'Corrections: reply in the meeting thread within two business days; otherwise these minutes stand as the record of July 9, 2026.', size: 9.5, weight: 400, color: FAINT, lineHeight: 1.5 }),
        ],
      },
    ],
  },

  /* ════════════════ 10. MEDIA KIT / ONE-PAGER — plum ════════════════ */
  {
    id: 'media-kit-pack',
    title: 'Studio Media Kit',
    shortLabel: 'Media Kit / One-Pager',
    description: 'Two-page capabilities kit: masthead with proof stats and services grid, then engagement models with pricing, process, and a contact panel.',
    icon: 'fas fa-address-card',
    composerPresetId: 'doc-media-kit',
    pages: [
      {
        label: 'Studio One-Pager',
        ...LETTER_PAGE,
        layers: [
          rect({ name: 'Masthead Panel', left: 0, top: 0, w: PAGE_W, h: 264, fill: PLUM }),
          text({ name: 'Masthead Eyebrow', left: M, top: 56, width: 480, text: 'CAPABILITIES · MEDIA KIT', size: 10.5, weight: 700, color: '#c4b8e0', tracking: 2.6 }),
          text({ name: 'Masthead Title', left: M, top: 84, width: 660, text: 'Atlas Creative Studio', size: 36, weight: 800, color: '#ffffff' }),
          para({ name: 'Masthead Tagline', left: M, top: 140, width: 600, height: 42, text: 'A senior design and growth studio for ambitious consumer brands — strategy, storefronts, and the media that feeds them.', size: 12.5, weight: 400, color: '#d9d2ec', lineHeight: 1.55 }),
          text({ name: 'Masthead Contact', left: M, top: 202, width: 640, text: 'atlascreative.co · hello@atlascreative.co · (415) 555-0134 · San Francisco', size: 10, weight: 500, color: '#b3a6d6' }),
          ...kpiCard({ name: 'Proof Projects', left: 64, top: 300, w: 163, h: 96, label: 'Projects shipped', value: '87', delta: 'Since 2015', deltaColor: MUTED, accent: PLUM }),
          ...kpiCard({ name: 'Proof ROAS', left: 239, top: 300, w: 163, h: 96, label: 'Avg. blended ROAS', value: '6.2x', delta: 'Active retainers', deltaColor: MUTED, accent: PLUM }),
          ...kpiCard({ name: 'Proof Rating', left: 414, top: 300, w: 163, h: 96, label: 'Client rating', value: '4.9 / 5', delta: '61 post-project surveys', deltaColor: MUTED, accent: PLUM, valueSize: 21 }),
          ...kpiCard({ name: 'Proof Years', left: 589, top: 300, w: 163, h: 96, label: 'Years running', value: '11', delta: 'Same three leads', deltaColor: MUTED, accent: PLUM }),
          ...sectionLabel({ label: 'What we do', top: 436, accent: PLUM }),
          ...[
            { title: 'Brand identity', body: 'Positioning, naming, visual systems' },
            { title: 'Web design & build', body: 'Marketing sites that load fast and convert' },
            { title: 'E-commerce', body: 'Shopify storefronts, CRO, merchandising' },
            { title: 'SEO & content', body: 'Technical foundations + editorial engines' },
            { title: 'Paid media', body: 'Full-funnel search & social, margin-gated' },
            { title: 'Lifecycle email', body: 'Klaviyo flows that carry 20%+ of revenue' },
          ].flatMap((service, i) => {
            const left = 64 + (i % 3) * 235;
            const top = 472 + Math.floor(i / 3) * 104;
            return [
              rect({ name: `Service ${i + 1} Card`, left, top, w: 218, h: 92, fill: CARD, radius: 8 }),
              rect({ name: `Service ${i + 1} Tick`, left, top, w: 3, h: 92, fill: PLUM }),
              text({ name: `Service ${i + 1} Title`, left: left + 16, top: top + 16, width: 186, text: service.title, size: 12, weight: 700, color: INK }),
              para({ name: `Service ${i + 1} Body`, left: left + 16, top: top + 38, width: 186, height: 40, text: service.body, size: 9.5, weight: 400, color: MUTED, lineHeight: 1.5 }),
            ];
          }),
          ...sectionLabel({ label: 'Select clients', top: 716, accent: PLUM }),
          para({ name: 'Clients Line', left: M, top: 752, width: CW, height: 40, text: 'Meridian Outdoor · Redwood Cycling · Bluestone Kitchen + Bar · Juniper Home Goods · Summit Physical Therapy · Harbor & Pine Realty', size: 11.5, weight: 500, color: BODY_COLOR, lineHeight: 1.6 }),
          rect({ name: 'Quote Rule', left: M, top: 820, w: CW, h: 1, fill: RULE }),
          para({ name: 'Kit Quote', left: M, top: 840, width: CW, height: 44, text: '“Every week had a split time and a decision.” — Casey Lombard, Founder, Redwood Cycling Co.', size: 12.5, weight: 400, color: '#4b5563', family: SERIF, italic: true, lineHeight: 1.55 }),
          rect({ name: 'Kit Footer Rule', left: M, top: 976, w: CW, h: 1, fill: RULE }),
          text({ name: 'Kit Footer Left', left: M, top: 990, width: 420, text: 'Atlas Creative Studio · Capabilities', size: 9, weight: 500, color: FAINT }),
          text({ name: 'Kit Footer Right', left: PAGE_W - M - 160, top: 990, width: 160, text: 'Page 1 of 2', size: 9, weight: 500, color: FAINT, align: 'right' }),
        ],
      },
      {
        label: 'Engagement & Contact',
        ...LETTER_PAGE,
        layers: [
          ...pageChrome({ accent: PLUM, docLabel: 'Atlas Creative Studio — Capabilities', footerLeft: 'Atlas Creative Studio · Capabilities', page: 2, total: 2 }),
          ...pageTitle({ kicker: 'Engagement models', title: 'Three ways to work with us', accent: PLUM }).layers,
          ...[
            { name: 'Sprint', price: '$9,500 fixed', frame: 'Two weeks · one sharp problem', includes: '•  Audit, prototype, or launch plan\n•  Daily async updates\n•  Executive readout + roadmap', highlight: false },
            { name: 'Project', price: 'from $25,000', frame: '6–12 weeks · full builds', includes: '•  Brand, site, or store builds\n•  Weekly demos, fixed fee\n•  30-day post-launch care', highlight: true },
            { name: 'Retainer', price: 'from $4,250 / mo', frame: 'Ongoing growth partner', includes: '•  Senior pod across channels\n•  Monthly scorecard reviews\n•  Quarterly roadmap resets', highlight: false },
          ].flatMap((tier, i) => {
            const left = 64 + i * 235;
            const cardTop = 208;
            return [
              rect({ name: `Tier ${tier.name} Card`, left, top: cardTop, w: 218, h: 224, fill: tier.highlight ? '#efeaf6' : CARD, radius: 10 }),
              rect({ name: `Tier ${tier.name} Tick`, left, top: cardTop, w: tier.highlight ? 218 : 3, h: tier.highlight ? 4 : 224, fill: PLUM }),
              ...(tier.highlight
                ? [
                  rect({ name: `Tier ${tier.name} Ribbon`, left: left + 54, top: cardTop - 12, w: 110, h: 24, fill: PLUM, radius: 12 }),
                  text({ name: `Tier ${tier.name} Ribbon Text`, left: left + 54, top: cardTop - 5, width: 110, text: 'MOST COMMON', size: 8, weight: 700, color: '#ffffff', align: 'center', tracking: 1 }),
                ]
                : []),
              text({ name: `Tier ${tier.name} Name`, left: left + 18, top: cardTop + 26, width: 182, text: tier.name.toUpperCase(), size: 10, weight: 700, color: PLUM, tracking: 1.8 }),
              text({ name: `Tier ${tier.name} Price`, left: left + 18, top: cardTop + 48, width: 182, text: tier.price, size: 17, weight: 800, color: INK }),
              text({ name: `Tier ${tier.name} Frame`, left: left + 18, top: cardTop + 76, width: 182, text: tier.frame, size: 9.5, weight: 600, color: MUTED }),
              lines({ name: `Tier ${tier.name} Includes`, left: left + 18, top: cardTop + 104, width: 182, text: tier.includes, size: 9.5, weight: 500, color: BODY_COLOR, lineHeight: 1.8 }),
            ];
          }),
          ...sectionLabel({ label: 'How it runs', top: 476, accent: PLUM }),
          ...[
            { n: '01', step: 'Intro call', detail: '30 minutes, no deck' },
            { n: '02', step: 'Proposal', detail: 'Scoped in five days' },
            { n: '03', step: 'Kickoff', detail: 'Within two weeks' },
            { n: '04', step: 'Weekly demos', detail: 'Progress you can click' },
          ].flatMap((processStep, i) => {
            const left = 64 + i * 174;
            return [
              text({ name: `Process ${processStep.n}`, left, top: 514, width: 40, text: processStep.n, size: 16, weight: 800, color: PLUM }),
              text({ name: `Process ${processStep.n} Step`, left, top: 540, width: 160, text: processStep.step, size: 11.5, weight: 700, color: INK }),
              text({ name: `Process ${processStep.n} Detail`, left, top: 560, width: 160, text: processStep.detail, size: 9.5, weight: 500, color: MUTED }),
            ];
          }),
          rect({ name: 'Process Rule', left: M, top: 596, w: CW, h: 1, fill: RULE }),
          ...sectionLabel({ label: 'Good-fit signals', top: 624, accent: PLUM }),
          ...[
            'You sell a physical or digital product consumers love — and want more of them to find it.',
            'You would rather see a weekly demo than a monthly slide deck.',
            'You want one senior team accountable for design and the numbers.',
          ].flatMap((signal, i) => [
            rect({ name: `Signal ${i + 1} Bullet`, left: M, top: 665 + i * 28, w: 5, h: 5, fill: PLUM }),
            text({ name: `Signal ${i + 1}`, left: 80, top: 660 + i * 28, width: 660, text: signal, size: 11, weight: 500, color: BODY_COLOR }),
          ]),
          rect({ name: 'Contact Panel', left: M, top: 764, w: CW, h: 132, fill: PLUM, radius: 10 }),
          text({ name: 'Contact Label', left: M + 28, top: 788, width: 400, text: 'START A CONVERSATION', size: 9, weight: 700, color: '#c4b8e0', tracking: 2 }),
          text({ name: 'Contact Email', left: M + 28, top: 812, width: 500, text: 'hello@atlascreative.co', size: 18, weight: 700, color: '#ffffff' }),
          text({ name: 'Contact Meta', left: M + 28, top: 846, width: 600, text: 'atlascreative.co · (415) 555-0134 · 548 Market Street, Suite 210, San Francisco', size: 10, weight: 500, color: '#d9d2ec' }),
          text({ name: 'Booking Note', left: PAGE_W - M - 220, top: 788, width: 192, text: 'BOOKING Q4 2026', size: 9, weight: 700, color: '#ffffff', align: 'right', tracking: 1.6 }),
        ],
      },
    ],
  },
];

/* ── Public API ───────────────────────────────────────────────────────── */

export const POLISHED_PDF_TEMPLATE_PRESETS: PolishedPdfTemplatePreset[] = TEMPLATE_SEEDS.map((seed) => ({
  id: seed.id,
  title: seed.title,
  shortLabel: seed.shortLabel,
  description: seed.description,
  icon: seed.icon,
  composerPresetId: seed.composerPresetId,
  pageCount: seed.pages.length,
}));

export function getPolishedPdfTemplatePreset(templateId: PolishedPdfTemplateId): PolishedPdfTemplatePreset | null {
  return POLISHED_PDF_TEMPLATE_PRESETS.find((preset) => preset.id === templateId) || null;
}

export function getPolishedPdfTemplatePageSummaries(templateId: PolishedPdfTemplateId): Array<{ label: string; layerCount: number }> | null {
  const seed = TEMPLATE_SEEDS.find((item) => item.id === templateId);
  if (!seed) return null;
  return seed.pages.map((page) => ({ label: page.label, layerCount: page.layers.length }));
}

/* ── FlowText preview synthesis (thumbnails only) ─────────────────────── */

/**
 * FlowText document layers render at draw time, so template page seeds for
 * flow-based documents (contract, NDA) only carry page chrome. For the
 * template preview modal we synthesize plain text layers that approximate the
 * flow pagination, giving the thumbnails real page content. These layers are
 * preview-only — buildPolishedPdfTemplate never includes them.
 */
function synthesizeFlowPreviewLayers(seed: TemplateSeed): Map<number, SeedLayer[]> {
  const byPage = new Map<number, SeedLayer[]>();
  if (!seed.buildDocumentLayers) return byPage;

  let entries: DocumentLayerEntry[] = [];
  try {
    entries = seed.buildDocumentLayers('preview');
  } catch {
    return byPage;
  }

  const push = (pageIndex: number, layer: SeedLayer) => {
    const list = byPage.get(pageIndex) ?? [];
    list.push(layer);
    byPage.set(pageIndex, list);
  };

  for (const entry of entries) {
    if (!entry || entry.type !== 'flowText') continue;
    const data = entry.data as unknown as Record<string, unknown>;
    const margins = (data.margins as { top: number; right: number; bottom: number; left: number } | undefined)
      ?? { top: FLOW_TOP, right: M, bottom: FLOW_BOTTOM, left: M };
    const overrides = (data.styleOverrides ?? {}) as Record<string, Record<string, unknown>>;
    const serif = typeof data.fontFamily === 'string' && /georgia|times|serif/i.test(data.fontFamily as string);
    const areaWidth = PAGE_W - margins.left - margins.right;
    const pageBottom = PAGE_H - margins.bottom;
    const maxPageIndex = seed.pages.length - 1;

    const baseStyles: Record<string, { size: number; mb: number; weight: number; lh: number; color: string }> = {
      heading1: { size: 27, mb: 8, weight: 700, lh: 1.25, color: INK },
      heading2: { size: 12.5, mb: 7, weight: 700, lh: 1.35, color: BODY_COLOR },
      heading3: { size: 12, mb: 6, weight: 600, lh: 1.35, color: INK },
      paragraph: { size: 12, mb: 11, weight: 400, lh: 1.62, color: '#1f2937' },
      bulletList: { size: 12, mb: 6, weight: 400, lh: 1.62, color: '#1f2937' },
      numberedList: { size: 12, mb: 6, weight: 400, lh: 1.62, color: '#1f2937' },
      blockquote: { size: 11.5, mb: 14, weight: 400, lh: 1.6, color: '#4b5563' },
    };
    const styleFor = (type: string) => {
      const base = baseStyles[type] ?? baseStyles.paragraph;
      const ov = overrides[type] ?? {};
      return {
        size: typeof ov.fontSize === 'number' ? ov.fontSize : base.size,
        mb: typeof ov.marginBottom === 'number' ? ov.marginBottom : base.mb,
        lh: typeof ov.lineHeight === 'number' ? ov.lineHeight : base.lh,
        weight: base.weight,
        color: typeof ov.color === 'string' ? ov.color : base.color,
      };
    };

    let pageIndex = Math.max(0, ((data.startPage as number | undefined) ?? 1) - 1);
    let cursorY = margins.top;
    let listNumber = 0;

    const blocks = Array.isArray(data.blocks) ? (data.blocks as Array<Record<string, unknown>>) : [];
    for (const block of blocks) {
      const type = String(block.type ?? 'paragraph');
      const runs = Array.isArray(block.runs) ? (block.runs as Array<{ text?: string }>) : [];
      const raw = runs.map((run) => run.text ?? '').join('');
      const style = styleFor(type);

      listNumber = type === 'numberedList' ? listNumber + 1 : 0;
      const display = type === 'bulletList' ? `•  ${raw}` : type === 'numberedList' ? `${listNumber}.  ${raw}` : raw;

      const isList = type === 'bulletList' || type === 'numberedList';
      const indent = isList ? 20 : 0;
      let blockHeight: number;
      if (type === 'divider') {
        blockHeight = 1 + 16;
      } else {
        const charWidth = style.size * (serif && !isList && type.startsWith('para') ? 0.5 : style.weight >= 600 ? 0.55 : 0.5);
        const estLines = Math.max(1, Math.ceil((raw.length * charWidth) / (areaWidth - indent)));
        blockHeight = estLines * style.size * style.lh + style.mb;
      }

      if (cursorY + blockHeight > pageBottom && pageIndex < maxPageIndex) {
        pageIndex += 1;
        cursorY = margins.top;
      }

      if (type === 'divider') {
        push(pageIndex, rect({ name: 'Flow Preview Divider', left: margins.left, top: Math.round(cursorY), w: areaWidth, h: 1, fill: '#cbd5e1' }));
      } else {
        push(pageIndex, {
          ...para({
            name: 'Flow Preview Text',
            left: margins.left + indent,
            top: Math.round(cursorY),
            width: areaWidth - indent,
            height: Math.max(14, Math.ceil(blockHeight - style.mb) + 2),
            text: display,
            size: style.size,
            weight: style.weight,
            color: style.color,
            family: serif && !type.startsWith('heading') ? SERIF : SANS,
            lineHeight: style.lh,
          }),
        });
      }
      cursorY += blockHeight;
    }
  }

  return byPage;
}

/**
 * Raw page data for thumbnail rendering.
 * Layers are returned in editor order (index 0 = topmost), matching what
 * TemplateThumbnail expects, and include synthesized FlowText preview layers
 * for flow-based documents.
 */
export function getPolishedPdfTemplatePageData(templateId: PolishedPdfTemplateId): Array<{ label: string; width: number; height: number; layers: Array<Record<string, unknown>> }> | null {
  const seed = TEMPLATE_SEEDS.find((item) => item.id === templateId);
  if (!seed) return null;
  const flowPreview = synthesizeFlowPreviewLayers(seed);
  return seed.pages.map((page, index) => ({
    label: page.label,
    width: page.width,
    height: page.height,
    layers: [...(flowPreview.get(index) ?? []), ...page.layers.slice().reverse()],
  }));
}

/* ── Seed → engine coordinate normalization ───────────────────────────── */

let textMeasureContext: CanvasRenderingContext2D | null | undefined;

function getTextMeasureContext(): CanvasRenderingContext2D | null {
  if (textMeasureContext !== undefined) return textMeasureContext;
  if (typeof document === 'undefined') {
    textMeasureContext = null;
    return textMeasureContext;
  }

  const canvas = document.createElement('canvas');
  textMeasureContext = canvas.getContext('2d');
  return textMeasureContext;
}

/**
 * Seed text layers are authored with x/y as the TOP-LEFT corner of the text
 * box (plus `__seedTextAnchor: 'top-left'`). The canvas engine positions text
 * boxes by their CENTER, so we measure the real layout and shift by half the
 * box size. In DOM-less environments (workers, tests) we fall back to a
 * deterministic estimate — constrained layers use maxWidth as the box width,
 * which matches the engine exactly for copy that fits.
 */
function normalizeSeedTextLayerAnchor(layer: Record<string, unknown>): void {
  if (layer.type !== 'text') return;
  if (layer.__seedTextAnchor !== 'top-left') return;
  if (typeof layer.x !== 'number' || typeof layer.y !== 'number') return;

  const font = typeof layer.font === 'number' ? layer.font : 22;
  const lineHeightMul = typeof layer.lineHeight === 'number' && layer.lineHeight > 0 ? layer.lineHeight : 1.2;
  const context = getTextMeasureContext();

  if (!context) {
    const rawText = typeof layer.text === 'string' ? layer.text : '';
    const textLines = rawText.split('\n');
    const longest = textLines.reduce((max, line) => Math.max(max, line.length), 0);
    const constrainedWidth = layer.constrain === true && typeof layer.maxWidth === 'number' && layer.maxWidth > 0 ? layer.maxWidth : 0;
    const estWidth = constrainedWidth > 0 ? constrainedWidth : Math.max(font, longest * font * 0.52);
    const estHeight = layer.mode === 'box' && typeof layer.boxHeight === 'number' && layer.boxHeight > 0
      ? layer.boxHeight
      : textLines.length * Math.round(font * lineHeightMul);
    layer.x = layer.x + estWidth / 2;
    layer.y = layer.y + estHeight / 2;
    delete layer.__seedTextAnchor;
    return;
  }

  const minFont = typeof layer.minFont === 'number' ? layer.minFont : font;
  const maxFont = typeof layer.maxFont === 'number' ? layer.maxFont : Math.max(font, minFont);
  const constrain = typeof layer.constrain === 'boolean' ? layer.constrain : true;
  const mode = layer.mode === 'box' ? 'box' : 'line';
  const hAlign = layer.hAlign === 'center' || layer.hAlign === 'right' ? layer.hAlign : 'left';
  const vAlign = layer.vAlign === 'middle' || layer.vAlign === 'bottom' ? layer.vAlign : 'top';
  const overflow =
    layer.overflow === 'clip' || layer.overflow === 'ellipsis' || layer.overflow === 'warn'
      ? layer.overflow
      : 'visible';
  const textDirection =
    layer.textDirection === 'rtl' || layer.textDirection === 'auto'
      ? layer.textDirection
      : 'ltr';

  const layoutProbe = {
    id: typeof layer.id === 'string' ? layer.id : 'seed-text',
    type: 'text',
    x: 0,
    y: 0,
    text: typeof layer.text === 'string' ? layer.text : '',
    font,
    fontFamily: typeof layer.fontFamily === 'string' ? layer.fontFamily : 'Inter',
    fontWeight: typeof layer.fontWeight === 'number' ? String(layer.fontWeight) : (typeof layer.fontWeight === 'string' ? layer.fontWeight : '700'),
    fontStyle: layer.fontStyle === 'italic' ? 'italic' : 'normal',
    color: typeof layer.color === 'string' ? layer.color : '000000',
    constrain,
    maxWidth: typeof layer.maxWidth === 'number' ? layer.maxWidth : 0,
    boxHeight: typeof layer.boxHeight === 'number' ? layer.boxHeight : 0,
    mode,
    resizeMode: layer.resizeMode === 'font' ? 'font' : 'box',
    minFont,
    maxFont,
    hAlign,
    vAlign,
    overflow,
    lineHeight: typeof layer.lineHeight === 'number' ? layer.lineHeight : undefined,
    letterSpacing: typeof layer.letterSpacing === 'number' ? layer.letterSpacing : undefined,
    strokeEnabled: layer.strokeEnabled === true,
    strokeWidth: typeof layer.strokeWidth === 'number' ? layer.strokeWidth : 0,
    strokeColor: typeof layer.strokeColor === 'string' ? layer.strokeColor : '000000',
    textDirection,
  } as CanvasEngineTextLayer;

  const layout = prepareTextLayout(layoutProbe, layoutProbe.text, context);
  if (!Number.isFinite(layout.maxLineWidth) || !Number.isFinite(layout.boxHeight)) {
    delete layer.__seedTextAnchor;
    return;
  }

  layer.x = layer.x + layout.maxLineWidth / 2;
  layer.y = layer.y + layout.boxHeight / 2;
  delete layer.__seedTextAnchor;
}

export function buildPolishedPdfTemplate(templateId: PolishedPdfTemplateId): BuiltPolishedPdfTemplate | null {
  const seed = TEMPLATE_SEEDS.find((item) => item.id === templateId);
  if (!seed) return null;

  const nonce = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  const pages: EditorPage[] = seed.pages.map((page, pageIndex) => ({
    id: `pdf-${seed.id}-${nonce}-p${pageIndex + 1}`,
    label: page.label,
    width: page.width,
    height: page.height,
  }));

  const layersByPageId = pages.reduce<Record<string, TemplateLayer[]>>((acc, page, pageIndex) => {
    const pageSeed = seed.pages[pageIndex];
    const clonedLayers = pageSeed.layers.map((layer, layerIndex) => {
      const clone = JSON.parse(JSON.stringify(layer)) as Record<string, unknown>;
      normalizeSeedTextLayerAnchor(clone);
      clone.id = `layer-${seed.id}-${nonce}-p${pageIndex + 1}-l${layerIndex + 1}`;
      return clone as TemplateLayer;
    });
    // Seed definitions are authored in painter order (back to front). The editor
    // runtime stores layers top-first and reverses before drawing.
    acc[page.id] = clonedLayers.slice().reverse();
    return acc;
  }, {});

  // Use seed-provided document layers (e.g. FlowText for contract body text)
  // or fall back to an empty array for templates without FlowText.
  const documentLayers: DocumentLayerEntry[] = seed.buildDocumentLayers?.(nonce) ?? [];

  return {
    id: seed.id,
    title: seed.title,
    composerPresetId: seed.composerPresetId,
    pages,
    layersByPageId,
    documentLayers,
  };
}

export function getPolishedPdfTemplateIdByComposerPreset(composerPresetId: string): PolishedPdfTemplateId | null {
  const normalized = composerPresetId.trim();
  if (!normalized) return null;
  return TEMPLATE_SEEDS.find((seed) => seed.composerPresetId === normalized)?.id ?? null;
}

export function buildPolishedPdfTemplateFromComposerPreset(composerPresetId: string): BuiltPolishedPdfTemplate | null {
  const templateId = getPolishedPdfTemplateIdByComposerPreset(composerPresetId);
  return templateId ? buildPolishedPdfTemplate(templateId) : null;
}
